import { Koi } from '@ksyos/koi';
import {
    BezoekAanSpecialistOption,
    CompletenessOptions,
    ConclusieDiagnoseOption,
    SupervisorsAssessmentBiometrics,
    YesOrNo,
} from '@common/enums';
import type { ISchemaContainer } from '../schemaContainer';
import { asset, assets, type IAsset } from '../assets';
import { emailSchema } from '../schemas/emailSchema';

export const tfoV1FinalRequest = Koi.object().keys({
    andere_dm_complicaties: Koi.string().valid('Ja', 'Nee').allow(null),
    bloeddruk_diastolisch: Koi.numberAsString().min(0).max(500).allow(null),
    bloeddruk_systolisch: Koi.numberAsString().min(0).max(500).allow(null),
    diabetes_sinds: Koi.koi().date().allow(null),
    files: assets.optional(),
    hba1c: Koi.number().min(10).max(200).allow(null),
    oculaire_voorgeschiedenis: Koi.string().max(1000).allow(null),
    toelichting: Koi.when('andere_dm_complicaties', {
        is: 'Ja',
        otherwise: Koi.string().max(1000).allow(null),
        then: Koi.string().max(1000),
    }),
});

export const tfoV1DraftRequest = tfoV1FinalRequest.concat(
    Koi.object({
        diabetes_sinds: Koi.any().allow(null),
        toelichting: Koi.any().allow(null),
    })
);

const hoeGemetenValidation = Koi.string().valid(
    'Visus SC (zonder correctie)',
    'Visus CC (met correctie)',
    'Visus BCVA (optimale correctie)',
    'Visus EC (eigen correctie)'
);

export enum OphthalmologyFilePlacement {
    aanvullend = 'aanvullend',
    od = 'od',
    os = 'os',
    verwijsbrief = 'verwijsbrief',
}

export const ophthalmologyFile = asset.concat(
    Koi.object({
        placement: Koi.koi().enum(OphthalmologyFilePlacement),
    })
);

export type TfoV1FinalProcedureSchema = {
    files: IOphthalmologyAsset[];
    hoe_gemeten: string | null;
    oogdruk_od: string | null;
    oogdruk_os: string | null;
    patient_gedruppeld: 'Ja' | 'Nee';
    visus_od: string | null;
    visus_os: string | null;
};

export const tfoV1FinalProcedure = Koi.object().keys({
    files: Koi.array().items(ophthalmologyFile),
    hoe_gemeten: Koi.when('visus_od', {
        is: null,
        otherwise: hoeGemetenValidation,
        then: Koi.when('visus_os', {
            is: null,
            otherwise: hoeGemetenValidation,
            then: Koi.valid(null),
        }),
    }),
    oogdruk_od: Koi.string().max(50).allow(null),
    oogdruk_os: Koi.string().max(50).allow(null),
    patient_gedruppeld: Koi.string().valid('Ja', 'Nee'),
    visus_od: Koi.string().max(50).allow(null),
    visus_os: Koi.string().max(50).allow(null),
});

interface TfoV1DraftProcedureSchema extends Omit<TfoV1FinalProcedureSchema, 'patient_gedruppeld'> {
    patient_gedruppeld: TfoV1FinalProcedureSchema['patient_gedruppeld'] | null;
}

export const tfoV1DraftProcedure = tfoV1FinalProcedure.concat(
    Koi.object({
        hoe_gemeten: Koi.any().allow(null),
        patient_gedruppeld: Koi.any().allow(null),
    })
);

const bevindingenNpdrOptions = ['Geen', 'Gering', 'Matig', 'Ernstig', 'Zeer ernstig', 'Niet te beoordelen'] as const;

export type TfoOldScreeningStandard = (typeof bevindingenNpdrOptions)[number];

export const urgenciesAccordingToGuideline = [
    '3 jaar (reguliere controle)',
    '2 jaar (reguliere controle)',
    '1 jaar (reguliere controle)',
] as const;

export const urgenciesForReferral = [
    'Binnen 6 maanden bij oogarts',
    'Binnen 3 maanden bij oogarts',
    'Binnen 1 maand bij oogarts',
    'Binnen 1 week bij oogarts',
    'Binnen 1 dag bij oogarts',
] as const;

export enum Retinopathie {
    nietTeBeoordelen = 'Niet te beoordelen',
    r0 = 'R0: Geen zichtbare retinopathie',
    r1 = 'R1: Milde achtergrondretinopathie',
    r2 = 'R2: Pre-proliferatieve retinopathie',
    r3 = 'R3: Proliferatieve retinopathie',
}

export enum Maculopathie {
    m0 = 'M0: Geen zichtbare maculopathie',
    m1 = 'M1: Zichtbare maculopathie',
    nietTeBeoordelen = 'Niet te beoordelen',
}

export enum Policies {
    anders = 'Anders',
    controleVolgensRichtlijn = 'Controle volgens richtlijn',
    teleConsultOogarts = 'Digitaal consult oogarts',
    vervolgonderzoekOptometrist = 'Vervolgonderzoek optometrist',
    verwijzingOogartsDoorHuisarts = 'Verwijzing naar oogarts door huisarts',
    verwijzingOogartsDoorOptometrist = 'Verwijzing naar oogarts door optometrist',
}

export const retinopathieOptions = [
    Retinopathie.r0,
    Retinopathie.r1,
    Retinopathie.r2,
    Retinopathie.r3,
    Retinopathie.nietTeBeoordelen,
];

export const retinopathieOptionsForMaculopathieM1 = [
    Retinopathie.r1,
    Retinopathie.r2,
    Retinopathie.r3,
    Retinopathie.nietTeBeoordelen,
];

const retinopathieOdValidation = Koi.when('maculopathie_od', {
    is: Maculopathie.m1,
    otherwise: Koi.koi().enum(Retinopathie),
    then: Koi.valid(...retinopathieOptionsForMaculopathieM1),
});

const retinopathieOsValidation = Koi.when('maculopathie_os', {
    is: Maculopathie.m1,
    otherwise: Koi.koi().enum(Retinopathie),
    then: Koi.valid(...retinopathieOptionsForMaculopathieM1),
});

interface FundusScreeningUrgencyValidationSchema {
    urgentie: string | null;
}

const fundusscreeningUrgencyValidation = Koi.object().keys({
    urgentie: Koi.when('geadviseerd_beleid', {
        is: Policies.controleVolgensRichtlijn,
        otherwise: Koi.when('geadviseerd_beleid', {
            is: Koi.valid(Policies.verwijzingOogartsDoorHuisarts, Policies.verwijzingOogartsDoorOptometrist),
            otherwise: Koi.valid(null),
            then: Koi.valid(...urgenciesForReferral),
        }),
        then: Koi.valid(...urgenciesAccordingToGuideline),
    }),
});

interface TfoV1FinalDiagnosticReportSupervisionCommon {
    andere_bevindingen_od: boolean | null;
    andere_bevindingen_od_text: string | null;
    andere_bevindingen_os: boolean | null;
    andere_bevindingen_os_text: string | null;
}

const tfoV1FinalDiagnosticReportSupervisionCommon = Koi.object()
    .keys({
        andere_bevindingen_od: Koi.boolean().allow(null),
        andere_bevindingen_od_text: Koi.when('andere_bevindingen_od', {
            is: true,
            otherwise: null,
            then: Koi.string().max(255).required(),
        }),
        andere_bevindingen_os: Koi.boolean().allow(null),
        andere_bevindingen_os_text: Koi.when('andere_bevindingen_os', {
            is: true,
            otherwise: null,
            then: Koi.string().max(255).required(),
        }),
    })
    .options({ stripUnknown: true });

interface TfoV1FinalDiagnosticReportBaseSchema
    extends TfoV1FinalDiagnosticReportSupervisionCommon,
        FundusScreeningUrgencyValidationSchema {
    geadviseerd_beleid: Policies | null;
    geadviseerd_beleid_anders: string | null;
    toelichting: string | null;
}

const tfoV1FinalDiagnosticReportBase = tfoV1FinalDiagnosticReportSupervisionCommon
    .concat(fundusscreeningUrgencyValidation)
    .concat(
        Koi.object().keys({
            geadviseerd_beleid: Koi.koi().enum(Policies),
            geadviseerd_beleid_anders: Koi.when('geadviseerd_beleid', {
                is: Policies.anders,
                otherwise: Koi.valid(null),
                then: Koi.string().max(255).required(),
            }),
            toelichting: Koi.string().max(1000).allow(null),
        })
    );

interface TfoV1FinalDiagnosticReportScreeningSchema {
    maculopathie_od: Maculopathie;
    maculopathie_os: Maculopathie;
    retinopathie_od: Retinopathie;
    retinopathie_os: Retinopathie;
}

const tfoV1FinalDiagnosticReportScreening = Koi.object().keys({
    maculopathie_od: Koi.koi().enum(Maculopathie),
    maculopathie_os: Koi.koi().enum(Maculopathie),
    retinopathie_od: retinopathieOdValidation,
    retinopathie_os: retinopathieOsValidation,
});

interface TfoV1FinalProcedureDiagnosticReportSchema
    extends TfoV1FinalDiagnosticReportBaseSchema,
        TfoV1FinalDiagnosticReportScreeningSchema,
        TfoV1FinalProcedureSchema {}

export const tfoV1FinalDiagnosticReport = tfoV1FinalDiagnosticReportBase.concat(tfoV1FinalDiagnosticReportScreening);

export interface TfoV1ProcedureDiagnosticReportAddEditFinalSchema extends TfoV1FinalProcedureDiagnosticReportSchema {}

export const tfoV1ProcedureDiagnosticReportAddEditFinalSchema: ISchemaContainer<TfoV1ProcedureDiagnosticReportAddEditFinalSchema> =
    {
        schema: tfoV1FinalDiagnosticReport.concat(tfoV1FinalProcedure).concat(
            Koi.object({
                draftCareProviderOrganizationId: Koi.string().uuid().required(),
                draftCareProviderPractitionerId: Koi.string().uuid().required(),
            })
        ),
    };

interface TfoV1DiagnosticReportAddendumAddSchema
    extends TfoV1FinalDiagnosticReportBaseSchema,
        TfoV1FinalDiagnosticReportScreeningSchema {}

export const tfV1DiagnosticReportAddendumAddSchema: ISchemaContainer<TfoV1DiagnosticReportAddendumAddSchema> = {
    schema: tfoV1FinalDiagnosticReport,
};

export const tfoV1SupervisionCustomControlDataBaseDraft = Koi.object().keys({
    andere_bevindingen_od: Koi.any().allow(null),
    andere_bevindingen_od_text: Koi.any().allow(null),
    andere_bevindingen_os: Koi.any().allow(null),
    andere_bevindingen_os_text: Koi.any().allow(null),
    geadviseerd_beleid: Koi.any().allow(null),
    geadviseerd_beleid_anders: Koi.any().allow(null),
    toelichting_kwaliteitscontrole: Koi.object({
        anders_namelijk: Koi.string().max(255).allow(null),
        cataract: Koi.boolean().allow(null),
        niet_gedruppeld: Koi.boolean().allow(null),
    }),
    urgentie: Koi.any().allow(null),
});

export type TfoV1DraftDiagnosticReportSchema = {
    andere_bevindingen_od: boolean | null;
    andere_bevindingen_od_text: string | null;
    andere_bevindingen_os: boolean | null;
    andere_bevindingen_os_text: string | null;
    geadviseerd_beleid: Policies | null;
    geadviseerd_beleid_anders: string | null;
    maculopathie_od: Maculopathie | null;
    maculopathie_os: Maculopathie | null;
    retinopathie_od: Retinopathie | null;
    retinopathie_os: Retinopathie | null;
    toelichting: string | null;
    urgentie: string | null;
};

export const tfoV1DraftDiagnosticReport = Koi.object().keys({
    andere_bevindingen_od: Koi.any().allow(null),
    andere_bevindingen_od_text: Koi.any().allow(null),
    andere_bevindingen_os: Koi.any().allow(null),
    andere_bevindingen_os_text: Koi.any().allow(null),
    geadviseerd_beleid: Koi.any().allow(null),
    geadviseerd_beleid_anders: Koi.any().allow(null),
    maculopathie_od: Koi.any().optional(),
    maculopathie_os: Koi.any().optional(),
    retinopathie_od: Koi.any().optional(),
    retinopathie_os: Koi.any().optional(),
    toelichting: Koi.string().max(1000).allow(null),
    urgentie: Koi.any().allow(null),
});

export const tfoV1DraftDiagnosticReportSchema: ISchemaContainer<TfoV1DraftDiagnosticReportSchema> = {
    schema: tfoV1DraftDiagnosticReport,
};

export interface TfoV1ProcedureDiagnosticReportAddEditDraftSchema
    extends TfoV1DraftDiagnosticReportSchema,
        TfoV1DraftProcedureSchema {}

export const tfoV1ProcedureDiagnosticReportAddEditDraftSchema: ISchemaContainer<TfoV1ProcedureDiagnosticReportAddEditDraftSchema> =
    {
        schema: tfoV1DraftDiagnosticReport.concat(tfoV1DraftProcedure),
    };

export const huisartsBetrokkenheid = {
    doorlopendAfspraken: 'Met deze huisarts(praktijk) bestaan doorlopende afspraken',
    mondelingeVerwijzing: 'Patiënt heeft een mondelinge verwijzing van de huisarts',
    schriftelijkeVerwijzing: 'Patiënt heeft een schriftelijke verwijzing van de huisarts',
} as const;

export const tooV1RedenVanVerwijzing = Koi.object().keys({
    andersText: Koi.string().max(1000).allow(null),
    dubbel_zien: Koi.boolean().allow(null),
    gezichtsveldklachten: Koi.boolean().allow(null),
    hoofdpijn: Koi.boolean().allow(null),
    prikken_jeuken_tranen: Koi.boolean().allow(null),
    risicofactoren_glaucoom: Koi.boolean().allow(null),
    rood_oog_droog_oog: Koi.boolean().allow(null),
    verdenking_op_cataract: Koi.boolean().allow(null),
    vervormd_zien: Koi.boolean().allow(null),
    visusveranderingen: Koi.boolean().allow(null),
    wazig_zien: Koi.boolean().allow(null),
});

export const tooV1DraftRequest = Koi.object().keys({
    familiaire_voorgeschiedenis: Koi.string().max(10000).allow(null),
    files: assets.optional(),
    isPatientInformed: Koi.boolean(),
    oculaire_voorgeschiedenis: Koi.string().max(10000).allow(null),
    omschrijf_de_duur_van_de_klachten: Koi.string().max(10000).allow(null),
    patientEmailAddress: Koi.string().max(1000).allow(null).optional(),
    reden_van_verwijzing: tooV1RedenVanVerwijzing,
    relevante_medische_voorgeschiedenis: Koi.string().max(10000).allow(null),
    toelichting: Koi.string().max(10000).allow(null),
});

export const tooV1FinalRequest = tooV1DraftRequest
    .concat(
        Koi.object().keys({
            patientEmailAddress: emailSchema.allow(null).optional(),
        })
    )
    .concat(
        Koi.object().keys({
            reden_van_verwijzing: tooV1RedenVanVerwijzing.anyFilledIn(),
        })
    );

const toelichtingKwaliteitscontroleValidation = Koi.object({
    anders_namelijk: Koi.string().max(255).allow(null),
    cataract: Koi.boolean(),
    niet_gedruppeld: Koi.boolean(),
});

const tfoV1SupervisionCustomControlDataBase = tfoV1FinalDiagnosticReportSupervisionCommon.concat(
    Koi.object().keys({
        toelichting_kwaliteitscontrole: toelichtingKwaliteitscontroleValidation,
    })
);

export const tfoV1SupervisionCustomControlData = tfoV1SupervisionCustomControlDataBase.concat(
    tfoV1FinalDiagnosticReportScreening
);

export const tfoV1SupervisionCustomControlDataDraft = tfoV1SupervisionCustomControlDataBase.concat(
    Koi.object({
        maculopathie_od: Koi.koi().enum(Maculopathie).optional(),
        maculopathie_os: Koi.koi().enum(Maculopathie).optional(),
        retinopathie_od: retinopathieOdValidation.optional(),
        retinopathie_os: retinopathieOsValidation.optional(),
    })
);

export const TooOphthalmologyPoliciesEnum = {
    anders: 'Anders',
    consultOogartsDoorHuisarts: 'Verwijzing naar oogarts door huisarts',
    consultOogartsDoorOptometrist: 'Verwijzing naar oogarts door optometrist',
    geenVervolgactie: 'Geen vervolgactie',
    revisieOptometrist: 'Revisie optometrist',
    terugverwijzingNaarHuisarts: 'Terugverwijzing naar huisarts',
    verwijzingOrthoptist: 'Verwijzing orthoptist',
} as const;

export type TooOphthalmologyPolicies = (typeof TooOphthalmologyPoliciesEnum)[keyof typeof TooOphthalmologyPoliciesEnum];

export const OphthalmologyPoliciesEnum = {
    ...TooOphthalmologyPoliciesEnum,
    akkoordMetBeleidOptometrist: 'Akkoord met voorgestelde beleid optometrist',
} as const;

type OphthalmologyPolicies = (typeof OphthalmologyPoliciesEnum)[keyof typeof OphthalmologyPoliciesEnum];

export type UrgenciesForRevisionOptometrist = string;

export const UrgenciesForRevisionOptometristRegex =
    /^((1 (dag|week|maand|jaar))|(([2-9]|([1-9]\d)) (dagen|weken|maanden|jaar)))$/;

export const UrgenciesForConsultOogartsNoAndersEnum = {
    a1_spoed_binnen_24_uur: 'A1. Spoed binnen 24 uur',
    a2_spoed_binnen_2_tot_4_werkdagen: 'A2. Spoed binnen 2 tot 4 werkdagen',
    b_voorrangsverwijzing: 'B. Voorrangsverwijzing, binnen 2 weken',
    c1_consult_gewone_verwijzing: 'C1. Consult gewone verwijzing, binnen 1-3 maanden',
    c2_consult_gewone_verwijzing: 'C2. Consult gewone verwijzing, binnen 4-6 maanden',
} as const;

export type UrgenciesForConsultOogartsNoAnders =
    (typeof UrgenciesForConsultOogartsNoAndersEnum)[keyof typeof UrgenciesForConsultOogartsNoAndersEnum];

export const UrgenciesForConsultOogartsEnum = {
    ...UrgenciesForConsultOogartsNoAndersEnum,
    anders: 'Anders',
} as const;

export type UrgenciesForConsultOogarts =
    (typeof UrgenciesForConsultOogartsEnum)[keyof typeof UrgenciesForConsultOogartsEnum];

const bevindingenValidation = Koi.object().keys({
    andersText: Koi.string().max(10000).allow(null),
    andersText_od: Koi.boolean().allow(null),
    andersText_os: Koi.boolean().allow(null),
    geen_afwijkingen: Koi.boolean().allow(null),
    geen_afwijkingen_od: Koi.boolean().allow(null),
    geen_afwijkingen_os: Koi.boolean().allow(null),
    niet_te_beoordelen: Koi.boolean().allow(null),
    niet_te_beoordelen_od: Koi.boolean().allow(null),
    niet_te_beoordelen_os: Koi.boolean().allow(null),
});

const werkdiagnoseValidation = Koi.object().keys({
    cataract: Koi.boolean().allow(null),
    diabetische_retinopathie: Koi.boolean().allow(null),
    droge_ogen: Koi.boolean().allow(null),
    glaucoom: Koi.boolean().allow(null),
    glaucoom_ja: Koi.boolean().allow(null),
    glaucoom_nee: Koi.boolean().allow(null),
    glaucoom_suspect: Koi.boolean().allow(null),
    macula_degeneratie: Koi.boolean().allow(null),
    macula_droog: Koi.boolean().allow(null),
    macula_nat: Koi.boolean().allow(null),
    macula_suspect: Koi.boolean().allow(null),
    retinopathie: Koi.boolean().allow(null),
    retinopathie_overig: Koi.boolean().allow(null),
});

const supervisieFinalValidation = Koi.object().keys({
    assessmentBiometrics: Koi.koi().enum(SupervisorsAssessmentBiometrics).required(),
    assessmentCompleteness: Koi.koi().enum(CompletenessOptions).required(),
    assessmentDiagnosis: Koi.koi().enum(SupervisorsAssessmentBiometrics).required(),
    assessmentQuestion: Koi.koi().enum(SupervisorsAssessmentBiometrics).required(),
    remarkBiometrics: Koi.string().max(10000).allow(null),
    remarkGrading: Koi.string().max(10000).allow(null),
});

export const tocV1FinalDiagnosticReport = Koi.object().keys({
    antwoord: Koi.string().max(1000).allow(null),
    beleid: Koi.koi().enum(OphthalmologyPoliciesEnum),
    beleid_anders: Koi.when('beleid', {
        is: OphthalmologyPoliciesEnum.anders,
        otherwise: Koi.valid(null),
        then: Koi.string().max(255).required(),
    }),
    bevindingen: bevindingenValidation,
    bezoek_aan_specialist_nu_nodig: Koi.string().max(1000).required(),
    hasSupervisionFields: Koi.boolean(),
    heb_je_een_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).required(),
    supervision: Koi.when('hasSupervisionFields', {
        is: true,
        otherwise: Koi.any().optional().allow(null),
        then: supervisieFinalValidation,
    }),
    urgentie: Koi.when('beleid', {
        is: OphthalmologyPoliciesEnum.revisieOptometrist,
        otherwise: Koi.when('beleid', {
            is: Koi.valid(
                OphthalmologyPoliciesEnum.consultOogartsDoorHuisarts,
                OphthalmologyPoliciesEnum.consultOogartsDoorOptometrist
            ),
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(UrgenciesForConsultOogartsEnum),
        }),
        then: Koi.string().regex(UrgenciesForRevisionOptometristRegex),
    }),
    urgentie_anders: Koi.when('urgentie', {
        is: UrgenciesForConsultOogartsEnum.anders,
        otherwise: Koi.valid(null),
        then: Koi.string().max(255).required(),
    }),
    werkdiagnose: werkdiagnoseValidation,
});

const supervisieDraftValidation = Koi.object().keys({
    assessmentBiometrics: Koi.any().allow(null),
    assessmentCompleteness: Koi.any().allow(null),
    assessmentDiagnosis: Koi.any().allow(null),
    assessmentQuestion: Koi.any().allow(null),
    remarkBiometrics: Koi.string().max(10000).allow(null),
    remarkGrading: Koi.string().max(10000).allow(null),
});

export const tocV1DraftDiagnosticReport = tocV1FinalDiagnosticReport.concat(
    Koi.object().keys({
        beleid: Koi.koi().enum(OphthalmologyPoliciesEnum).allow(null),
        beleid_anders: Koi.any().allow(null),
        bezoek_aan_specialist_nu_nodig: Koi.string().max(1000).allow(null),
        hasSupervisionFields: Koi.boolean(),
        heb_je_een_diagnose: Koi.any().allow(null),
        supervision: Koi.when('hasSupervisionFields', {
            is: true,
            otherwise: Koi.any().optional().allow(null),
            then: supervisieDraftValidation,
        }),
        urgentie: Koi.any().allow(null),
        urgentie_anders: Koi.any().allow(null),
    })
);

export enum TooVerwijzer {
    collegaOptometrist = 'Collega-optometrist',
    huisarts = 'Huisarts',
    internist = 'Internist',
    neuroloog = 'Neuroloog',
    oogarts = 'Oogarts',
    opInitiatiefVanDePatient = 'Op initiatief van de patiënt',
    opticien = 'Opticien',
    optometrist = 'Optometrist',
    overig = 'Overig',
}

export enum TooProtocol {
    basisonderzoek = 'Basisonderzoek',
    cataract = 'Cataract',
    glaucoom = 'Glaucoom',
    maculaDegeneratie = 'Macula degeneratie',
}

export enum TooRedenBezoek {
    acuteVisusdaling = 'Acute visusdaling',
    afsluitbareKamerhoek = 'Afsluitbare kamerhoek',
    anders = 'Anders',
    asthenopeKlanchten = 'Asthenope klachten (hoofdpijn, vermoeide ogen, trekkende ogen, etc)',
    aziatische = 'Aziatische of negroïde afkomst',
    bestaandeClient = 'Bestaande client met visusklachten',
    dubbleZien = 'Dubbel zien',
    filtsen = 'Flitsen en/of vlekken',
    glaucoom = 'Glaucoom in de familie',
    irritatie = 'Irritatie (prikken, jeuken, tranen, droge oog, rood oog) - NHG richtlijn',
    leeftijd = 'Leeftijd > 39 jaar',
    medicijngebruik = 'Medicijngebruik',
    metamorfopsie = 'Metamorfopsie',
    niwewClient = 'Nieuwe client met visusklachten',
    oogdruk = 'Oogdruk > 24 MmHg met applanatie tonometer',
    ooggezondheidsonderzoek = 'Ooggezondheidsonderzoek (OGO)',
    periodiekeControle = 'Periodieke controle',
    retinaOnderzoek = 'Retina onderzoek (non DRP)',
    suspectePapil = 'Suspecte papil',
    verdenkingAge = 'Verdenking op age related macula degeneratie',
    verdenkingCataract = 'Verdenking op cataract',
    verschilTensie = 'Verschil in tensie > 5 mmHg',
    visusLagerDan08 = 'Visus lager dan 0,8 (best corrected visual acuity op een oog en niet te corrigeren met een bril, uitgezonderd amblyopie',
}

export const tooHoeGemetenOptions = [
    'Visus SC (zonder correctie)',
    'Visus CC (met correctie)',
    'Visus BCVA (optimale correctie)',
    'Visus EC (eigen correctie)',
];
export enum TooGonioscope {
    afsluitbaar = 'Afsluitbaar',
    gesloten = 'Gesloten',
    nauw = 'Nauw',
    open = 'Open',
}
export enum TooAmslerTest {
    geenAfwijkingen = 'Geen afwijkingen',
    metamorfopsie = 'Metamorfopsie',
}

export const tocV1FinalRequest = Koi.object().keys({
    evaluation: Koi.object({
        otherwiseReferPatient: Koi.koi().enum(YesOrNo).allow(null),
    }),
    question: Koi.string().max(1000),
    selectedDiagnosticReportProviderOrganizationId: Koi.string().uuid(),
    selectedDiagnosticReportProviderPractitionerId: Koi.string().uuid().allow(null),
    toelichting: Koi.string().max(1000).allow(null),
});

export const tocV1DraftRequest = tocV1FinalRequest.concat(
    Koi.object().keys({
        question: Koi.string().max(1000).allow(null),
        selectedDiagnosticReportProviderOrganizationId: Koi.string().allow(null),
        selectedDiagnosticReportProviderPractitionerId: Koi.string().allow(null),
    })
);

export const tooV1FinalProcedure = Koi.object().keys({
    aanvullend_onderzoek: Koi.array().items(Koi.string().max(1000)).max(10),
    aanvullend_onderzoek_od: Koi.array().items(Koi.string().max(1000).allow(null)).max(10),
    aanvullend_onderzoek_os: Koi.array().items(Koi.string().max(1000).allow(null)).max(10),
    algemene_gezondheid_en_medicatie: Koi.string().max(1000).allow(null),
    amsler_test_od: Koi.when('protocol', {
        is: TooProtocol.maculaDegeneratie,
        otherwise: Koi.koi().enum(TooAmslerTest).allow(null),
        then: Koi.koi().enum(TooAmslerTest),
    }),
    amsler_test_os: Koi.when('protocol', {
        is: TooProtocol.maculaDegeneratie,
        otherwise: Koi.koi().enum(TooAmslerTest).allow(null),
        then: Koi.koi().enum(TooAmslerTest),
    }),
    autorefractie_as_od: Koi.string().max(50).allow(null),
    autorefractie_as_os: Koi.string().max(50).allow(null),
    autorefractie_cylindrisch_od: Koi.string().max(50).allow(null),
    autorefractie_cylindrisch_os: Koi.string().max(50).allow(null),
    autorefractie_sferisch_od: Koi.string().max(50).allow(null),
    autorefractie_sferisch_os: Koi.string().max(50).allow(null),
    break_up_time_od: Koi.string().max(50).allow(null),
    break_up_time_os: Koi.string().max(50).allow(null),
    consult: tocV1FinalRequest.optional(),
    cycloplegische_as_od: Koi.string().max(50).allow(null),
    cycloplegische_as_os: Koi.string().max(50).allow(null),
    cycloplegische_cylindrisch_od: Koi.string().max(50).allow(null),
    cycloplegische_cylindrisch_os: Koi.string().max(50).allow(null),
    cycloplegische_sferisch_od: Koi.string().max(50).allow(null),
    cycloplegische_sferisch_os: Koi.string().max(50).allow(null),
    draagt_correctie: Koi.boolean(),
    eigen_correctie_as_od: Koi.string().max(50).allow(null),
    eigen_correctie_as_os: Koi.string().max(50).allow(null),
    eigen_correctie_cylindrish_od: Koi.string().max(50).allow(null),
    eigen_correctie_cylindrish_os: Koi.string().max(50).allow(null),
    eigen_correctie_sferisch_od: Koi.string().max(50).allow(null),
    eigen_correctie_sferisch_os: Koi.string().max(50).allow(null),
    ervaart_klachten_als_storend: Koi.when('protocol', {
        is: TooProtocol.cataract,
        otherwise: Koi.boolean().allow(null),
        then: Koi.boolean(),
    }),
    familiaire_voorgeschiedenis: Koi.string().max(1000).allow(null),
    files: Koi.when('verwijsbrief_beschikbaar', {
        is: true,
        otherwise: Koi.array().items(ophthalmologyFile),
        then: Koi.array().items(ophthalmologyFile).min(1),
    }),
    gonioscopie_od: Koi.when('protocol', {
        is: TooProtocol.glaucoom,
        otherwise: Koi.koi().enum(TooGonioscope).allow(null),
        then: Koi.koi().enum(TooGonioscope),
    }),
    gonioscopie_os: Koi.when('protocol', {
        is: TooProtocol.glaucoom,
        otherwise: Koi.koi().enum(TooGonioscope).allow(null),
        then: Koi.koi().enum(TooGonioscope),
    }),
    gonioscopie_toelichting_od: Koi.string().max(1000).allow(null),
    gonioscopie_toelichting_os: Koi.string().max(1000).allow(null),
    huisarts_betrokkenheid: Koi.when('huisarts_is_betrokken', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().max(255).required(),
    }),
    huisarts_is_betrokken: Koi.boolean().allow(null),
    isPatientInformed: Koi.boolean(),
    klachten: Koi.string().max(1000).allow(null),
    nabije_visus_od: Koi.when('protocol', {
        is: TooProtocol.cataract,
        otherwise: Koi.string().max(50).allow(null),
        then: Koi.string().max(50),
    }),
    nabije_visus_os: Koi.when('protocol', {
        is: TooProtocol.cataract,
        otherwise: Koi.string().max(50).allow(null),
        then: Koi.string().max(50),
    }),
    oculaire_voorgeschiedenis: Koi.string().max(1000),
    oogdruk_od: Koi.string().max(50),
    oogdruk_os: Koi.string().max(50),
    pachymetrie_od: Koi.when('protocol', {
        is: TooProtocol.glaucoom,
        otherwise: Koi.string().max(50).allow(null),
        then: Koi.string().max(50),
    }),
    pachymetrie_os: Koi.when('protocol', {
        is: TooProtocol.glaucoom,
        otherwise: Koi.string().max(50).allow(null),
        then: Koi.string().max(50),
    }),
    protocol: Koi.koi().enum(TooProtocol),
    reden_van_bezoek: Koi.alternatives().try(Koi.koi().enum(TooRedenBezoek), Koi.string().max(100)),
    reden_van_bezoek_overig: Koi.when('reden_van_bezoek', {
        is: TooRedenBezoek.anders,
        otherwise: Koi.valid(null),
        then: Koi.string().max(1000),
    }),
    refractie_as_od: Koi.string().max(50).allow(null),
    refractie_as_os: Koi.string().max(50).allow(null),
    refractie_cylindrish_od: Koi.string().max(50).allow(null),
    refractie_cylindrish_os: Koi.string().max(50).allow(null),
    refractie_sferisch_od: Koi.string().max(50),
    refractie_sferisch_os: Koi.string().max(50),
    rijdt_auto: Koi.when('protocol', {
        is: TooProtocol.cataract,
        otherwise: Koi.boolean().allow(null),
        then: Koi.boolean(),
    }),
    stenopeische_visus_od: Koi.string().max(50),
    stenopeische_visus_os: Koi.string().max(50),
    verwijsbrief_beschikbaar: Koi.boolean().allow(null),
    visus_od: Koi.string().max(50),
    visus_os: Koi.string().max(50),
    visus_refractie_od: Koi.string().max(50),
    visus_refractie_os: Koi.string().max(50),
});

export const tooV1FinalProcedureWithRequest = tooV1FinalProcedure.concat(
    Koi.object().keys({
        verwijzer: Koi.koi().enum(TooVerwijzer).allow(null),
        verwijzer_overig: Koi.when('verwijzer', {
            is: TooVerwijzer.overig,
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000),
        }),
    })
);

export const tooV1DraftProcedure = Koi.object().keys({
    aanvullend_onderzoek: Koi.any().allow(null),
    aanvullend_onderzoek_od: Koi.any().allow(null),
    aanvullend_onderzoek_os: Koi.any().allow(null),
    algemene_gezondheid_en_medicatie: Koi.any().allow(null),
    amsler_test_od: Koi.any().allow(null),
    amsler_test_os: Koi.any().allow(null),
    autorefractie_as_od: Koi.any().allow(null),
    autorefractie_as_os: Koi.any().allow(null),
    autorefractie_cylindrisch_od: Koi.any().allow(null),
    autorefractie_cylindrisch_os: Koi.any().allow(null),
    autorefractie_sferisch_od: Koi.any().allow(null),
    autorefractie_sferisch_os: Koi.any().allow(null),
    break_up_time_od: Koi.any().allow(null),
    break_up_time_os: Koi.any().allow(null),
    consult: tocV1DraftRequest.optional(),
    cycloplegische_as_od: Koi.any().allow(null),
    cycloplegische_as_os: Koi.any().allow(null),
    cycloplegische_cylindrisch_od: Koi.any().allow(null),
    cycloplegische_cylindrisch_os: Koi.any().allow(null),
    cycloplegische_sferisch_od: Koi.any().allow(null),
    cycloplegische_sferisch_os: Koi.any().allow(null),
    draagt_correctie: Koi.any().allow(null),
    eigen_correctie_as_od: Koi.any().allow(null),
    eigen_correctie_as_os: Koi.any().allow(null),
    eigen_correctie_cylindrish_od: Koi.any().allow(null),
    eigen_correctie_cylindrish_os: Koi.any().allow(null),
    eigen_correctie_sferisch_od: Koi.any().allow(null),
    eigen_correctie_sferisch_os: Koi.any().allow(null),
    ervaart_klachten_als_storend: Koi.any().allow(null),
    familiaire_voorgeschiedenis: Koi.any().allow(null),
    files: Koi.array().items(ophthalmologyFile),
    gonioscopie_od: Koi.any().allow(null),
    gonioscopie_os: Koi.any().allow(null),
    gonioscopie_toelichting_od: Koi.any().allow(null),
    gonioscopie_toelichting_os: Koi.any().allow(null),
    huisarts_betrokkenheid: Koi.string().max(255).allow(null),
    huisarts_is_betrokken: Koi.boolean().allow(null),
    isPatientInformed: Koi.boolean(),
    klachten: Koi.any().allow(null),
    nabije_visus_od: Koi.any().allow(null),
    nabije_visus_os: Koi.any().allow(null),
    oculaire_voorgeschiedenis: Koi.any().allow(null),
    oogdruk_od: Koi.any().allow(null),
    oogdruk_os: Koi.any().allow(null),
    pachymetrie_od: Koi.any().allow(null),
    pachymetrie_os: Koi.any().allow(null),
    protocol: Koi.any().allow(null),
    reden_van_bezoek: Koi.any().allow(null),
    reden_van_bezoek_overig: Koi.any().allow(null),
    refractie_as_od: Koi.any().allow(null),
    refractie_as_os: Koi.any().allow(null),
    refractie_cylindrish_od: Koi.any().allow(null),
    refractie_cylindrish_os: Koi.any().allow(null),
    refractie_sferisch_od: Koi.any().allow(null),
    refractie_sferisch_os: Koi.any().allow(null),
    rijdt_auto: Koi.any().allow(null),
    stenopeische_visus_od: Koi.any().allow(null),
    stenopeische_visus_os: Koi.any().allow(null),
    verwijsbrief_beschikbaar: Koi.boolean().allow(null),
    verwijzer: Koi.any().allow(null),
    verwijzer_overig: Koi.any().allow(null),
    visus_od: Koi.any().allow(null),
    visus_os: Koi.any().allow(null),
    visus_refractie_od: Koi.any().allow(null),
    visus_refractie_os: Koi.any().allow(null),
});

const afwijkingInVoorsteOogsegmentValidation = Koi.object().keys({
    conjunctiva: Koi.boolean().allow(null),
    conjunctivaAndersText: Koi.when('conjunctivaOd_andersCheck', {
        is: true,
        otherwise: Koi.when('conjunctivaOs_andersCheck', {
            is: true,
            otherwise: Koi.allow(null),
            then: Koi.string().max(255),
        }),
        then: Koi.string().max(255),
    }),
    conjunctivaOd_andersCheck: Koi.boolean().allow(null),
    conjunctivaOd_chemosis: Koi.boolean().allow(null),
    conjunctivaOd_follikels: Koi.boolean().allow(null),
    conjunctivaOd_hyperemie: Koi.boolean().allow(null),
    conjunctivaOd_papillen: Koi.boolean().allow(null),
    conjunctivaOd_roodheid: Koi.boolean().allow(null),
    conjunctivaOd_subconjunctivaleBloeding: Koi.boolean().allow(null),
    conjunctivaOs_andersCheck: Koi.boolean().allow(null),
    conjunctivaOs_chemosis: Koi.boolean().allow(null),
    conjunctivaOs_follikels: Koi.boolean().allow(null),
    conjunctivaOs_hyperemie: Koi.boolean().allow(null),
    conjunctivaOs_papillen: Koi.boolean().allow(null),
    conjunctivaOs_roodheid: Koi.boolean().allow(null),
    conjunctivaOs_subconjunctivaleBloeding: Koi.boolean().allow(null),
    cornea: Koi.boolean().allow(null),
    corneaAndersText: Koi.when('corneaOd_andersCheck', {
        is: true,
        otherwise: Koi.when('corneaOs_andersCheck', {
            is: true,
            otherwise: Koi.allow(null),
            then: Koi.string().max(255),
        }),
        then: Koi.string().max(255),
    }),
    corneaOd_andersCheck: Koi.boolean().allow(null),
    corneaOd_corneadystrofie: Koi.boolean().allow(null),
    corneaOd_erosie: Koi.boolean().allow(null),
    corneaOd_fuchs: Koi.boolean().allow(null),
    corneaOd_guttata: Koi.boolean().allow(null),
    corneaOd_infiltraat: Koi.boolean().allow(null),
    corneaOd_keratoconus: Koi.boolean().allow(null),
    corneaOd_krukenbergSpindle: Koi.boolean().allow(null),
    corneaOd_mapDotFingerprintDystrofie: Koi.boolean().allow(null),
    corneaOd_neovascularisatie: Koi.boolean().allow(null),
    corneaOd_oedeem: Koi.boolean().allow(null),
    corneaOd_pterygium: Koi.boolean().allow(null),
    corneaOd_punctata: Koi.boolean().allow(null),
    corneaOd_ulcus: Koi.boolean().allow(null),
    corneaOs_andersCheck: Koi.boolean().allow(null),
    corneaOs_corneadystrofie: Koi.boolean().allow(null),
    corneaOs_erosie: Koi.boolean().allow(null),
    corneaOs_fuchs: Koi.boolean().allow(null),
    corneaOs_guttata: Koi.boolean().allow(null),
    corneaOs_infiltraat: Koi.boolean().allow(null),
    corneaOs_keratoconus: Koi.boolean().allow(null),
    corneaOs_krukenbergSpindle: Koi.boolean().allow(null),
    corneaOs_mapDotFingerprintDystrofie: Koi.boolean().allow(null),
    corneaOs_neovascularisatie: Koi.boolean().allow(null),
    corneaOs_oedeem: Koi.boolean().allow(null),
    corneaOs_pterygium: Koi.boolean().allow(null),
    corneaOs_punctata: Koi.boolean().allow(null),
    corneaOs_ulcus: Koi.boolean().allow(null),
    iris: Koi.boolean().allow(null),
    irisAndersText: Koi.when('irisOd_andersCheck', {
        is: true,
        otherwise: Koi.when('irisOs_andersCheck', {
            is: true,
            otherwise: Koi.allow(null),
            then: Koi.string().max(255),
        }),
        then: Koi.string().max(255),
    }),
    irisOd_andersCheck: Koi.boolean().allow(null),
    irisOd_diafanie: Koi.boolean().allow(null),
    irisOd_naevus: Koi.boolean().allow(null),
    irisOd_rubeosis: Koi.boolean().allow(null),
    irisOs_andersCheck: Koi.boolean().allow(null),
    irisOs_diafanie: Koi.boolean().allow(null),
    irisOs_naevus: Koi.boolean().allow(null),
    irisOs_rubeosis: Koi.boolean().allow(null),
    lens: Koi.boolean().allow(null),
    lensAndersText: Koi.when('lensOd_andersCheck', {
        is: true,
        otherwise: Koi.when('lensOs_andersCheck', {
            is: true,
            otherwise: Koi.allow(null),
            then: Koi.string().max(255),
        }),
        then: Koi.string().max(255),
    }),
    lensOd_andersCheck: Koi.boolean().allow(null),
    lensOd_cataract_beginnend: Koi.boolean().allow(null),
    lensOd_cataract_storend: Koi.boolean().allow(null),
    lensOd_lenssclerose: Koi.boolean().allow(null),
    lensOd_nastaar: Koi.boolean().allow(null),
    lensOd_pseudoexfoliate: Koi.boolean().allow(null),
    lensOd_pseudofaak: Koi.boolean().allow(null),
    lensOs_andersCheck: Koi.boolean().allow(null),
    lensOs_cataract_beginnend: Koi.boolean().allow(null),
    lensOs_cataract_storend: Koi.boolean().allow(null),
    lensOs_lenssclerose: Koi.boolean().allow(null),
    lensOs_nastaar: Koi.boolean().allow(null),
    lensOs_pseudoexfoliate: Koi.boolean().allow(null),
    lensOs_pseudofaak: Koi.boolean().allow(null),
    oogleden: Koi.boolean().allow(null),
    oogledenAndersText: Koi.when('oogledenOd_andersCheck', {
        is: true,
        otherwise: Koi.when('oogledenOs_andersCheck', {
            is: true,
            otherwise: Koi.allow(null),
            then: Koi.string().max(255),
        }),
        then: Koi.string().max(255),
    }),
    oogledenOd_MGD: Koi.boolean().allow(null),
    oogledenOd_andersCheck: Koi.boolean().allow(null),
    oogledenOd_blepharitis: Koi.boolean().allow(null),
    oogledenOd_chalazion: Koi.boolean().allow(null),
    oogledenOd_dermatochalasis: Koi.boolean().allow(null),
    oogledenOd_ectropion: Koi.boolean().allow(null),
    oogledenOd_entropion: Koi.boolean().allow(null),
    oogledenOd_hordeolum: Koi.boolean().allow(null),
    oogledenOd_ptosis: Koi.boolean().allow(null),
    oogledenOd_zwelling: Koi.boolean().allow(null),
    oogledenOs_MGD: Koi.boolean().allow(null),
    oogledenOs_andersCheck: Koi.boolean().allow(null),
    oogledenOs_blepharitis: Koi.boolean().allow(null),
    oogledenOs_chalazion: Koi.boolean().allow(null),
    oogledenOs_dermatochalasis: Koi.boolean().allow(null),
    oogledenOs_ectropion: Koi.boolean().allow(null),
    oogledenOs_entropion: Koi.boolean().allow(null),
    oogledenOs_hordeolum: Koi.boolean().allow(null),
    oogledenOs_ptosis: Koi.boolean().allow(null),
    oogledenOs_zwelling: Koi.boolean().allow(null),
    vok: Koi.boolean().allow(null),
    vokAndersText: Koi.when('vokOd_andersCheck', {
        is: true,
        otherwise: Koi.when('vokOs_andersCheck', {
            is: true,
            otherwise: Koi.allow(null),
            then: Koi.string().max(255),
        }),
        then: Koi.string().max(255),
    }),
    vokOd_andersCheck: Koi.boolean().allow(null),
    vokOd_cellen: Koi.boolean().allow(null),
    vokOd_tyndall: Koi.boolean().allow(null),
    vokOs_andersCheck: Koi.boolean().allow(null),
    vokOs_cellen: Koi.boolean().allow(null),
    vokOs_tyndall: Koi.boolean().allow(null),
});

const afwijkingInAchtersteOogsegmentValidation = Koi.object().keys({
    glasvocht: Koi.boolean().allow(null),
    glasvochtAndersText: Koi.when('glasvochtOd_andersCheck', {
        is: true,
        otherwise: Koi.when('glasvochtOs_andersCheck', {
            is: true,
            otherwise: Koi.allow(null),
            then: Koi.string().max(255),
        }),
        then: Koi.string().max(255),
    }),
    glasvochtOd_andersCheck: Koi.boolean().allow(null),
    glasvochtOd_exfoliatie: Koi.boolean().allow(null),
    glasvochtOd_glasvochtbloeding: Koi.boolean().allow(null),
    glasvochtOd_glasvochtloslating: Koi.boolean().allow(null),
    glasvochtOd_mouchesVolantes: Koi.boolean().allow(null),
    glasvochtOd_tobaccoDust: Koi.boolean().allow(null),
    glasvochtOs_andersCheck: Koi.boolean().allow(null),
    glasvochtOs_exfoliatie: Koi.boolean().allow(null),
    glasvochtOs_glasvochtbloeding: Koi.boolean().allow(null),
    glasvochtOs_glasvochtloslating: Koi.boolean().allow(null),
    glasvochtOs_mouchesVolantes: Koi.boolean().allow(null),
    glasvochtOs_tobaccoDust: Koi.boolean().allow(null),
    macula: Koi.boolean().allow(null),
    maculaAndersText: Koi.when('maculaOd_andersCheck', {
        is: true,
        otherwise: Koi.when('maculaOs_andersCheck', {
            is: true,
            otherwise: Koi.allow(null),
            then: Koi.string().max(255),
        }),
        then: Koi.string().max(255),
    }),
    maculaOd_andersCheck: Koi.boolean().allow(null),
    maculaOd_bloeding: Koi.boolean().allow(null),
    maculaOd_drusen: Koi.boolean().allow(null),
    maculaOd_macula_degeneratie: Koi.boolean().allow(null),
    maculaOd_maculopathie: Koi.boolean().allow(null),
    maculaOd_oedeem: Koi.boolean().allow(null),
    maculaOd_pigmentepitheel_alteraties: Koi.boolean().allow(null),
    maculaOd_pucker: Koi.boolean().allow(null),
    maculaOs_andersCheck: Koi.boolean().allow(null),
    maculaOs_bloeding: Koi.boolean().allow(null),
    maculaOs_drusen: Koi.boolean().allow(null),
    maculaOs_macula_degeneratie: Koi.boolean().allow(null),
    maculaOs_maculopathie: Koi.boolean().allow(null),
    maculaOs_oedeem: Koi.boolean().allow(null),
    maculaOs_pigmentepitheel_alteraties: Koi.boolean().allow(null),
    maculaOs_pucker: Koi.boolean().allow(null),
    papil: Koi.boolean().allow(null),
    papilAndersText: Koi.when('papilOd_andersCheck', {
        is: true,
        otherwise: Koi.when('papilOs_andersCheck', {
            is: true,
            otherwise: Koi.allow(null),
            then: Koi.string().max(255),
        }),
        then: Koi.string().max(255),
    }),
    papilOd_andersCheck: Koi.boolean().allow(null),
    papilOd_excavatie: Koi.boolean().allow(null),
    papilOd_glaucoomsuspect: Koi.boolean().allow(null),
    papilOd_neovascularisatie: Koi.boolean().allow(null),
    papilOd_oedeem: Koi.boolean().allow(null),
    papilOd_peripapillaireAtrofie: Koi.boolean().allow(null),
    papilOd_tilted: Koi.boolean().allow(null),
    papilOd_vlamvormigeBloeding: Koi.boolean().allow(null),
    papilOs_andersCheck: Koi.boolean().allow(null),
    papilOs_excavatie: Koi.boolean().allow(null),
    papilOs_glaucoomsuspect: Koi.boolean().allow(null),
    papilOs_neovascularisatie: Koi.boolean().allow(null),
    papilOs_oedeem: Koi.boolean().allow(null),
    papilOs_peripapillaireAtrofie: Koi.boolean().allow(null),
    papilOs_tilted: Koi.boolean().allow(null),
    papilOs_vlamvormigeBloeding: Koi.boolean().allow(null),
    perifere_retina: Koi.boolean().allow(null),
    perifere_retinaAndersText: Koi.when('perifere_retinaOd_andersCheck', {
        is: true,
        otherwise: Koi.when('perifere_retinaOs_andersCheck', {
            is: true,
            otherwise: Koi.allow(null),
            then: Koi.string().max(255),
        }),
        then: Koi.string().max(255),
    }),
    perifere_retinaOd_IRMA: Koi.boolean().allow(null),
    perifere_retinaOd_PEA: Koi.boolean().allow(null),
    perifere_retinaOd_ablatio: Koi.boolean().allow(null),
    perifere_retinaOd_andersCheck: Koi.boolean().allow(null),
    perifere_retinaOd_arterieleStamocclusie: Koi.boolean().allow(null),
    perifere_retinaOd_arterieleTakocclusie: Koi.boolean().allow(null),
    perifere_retinaOd_atherosclerosis: Koi.boolean().allow(null),
    perifere_retinaOd_beading: Koi.boolean().allow(null),
    perifere_retinaOd_bloeding: Koi.boolean().allow(null),
    perifere_retinaOd_cottonWoolSpots: Koi.boolean().allow(null),
    perifere_retinaOd_drusen: Koi.boolean().allow(null),
    perifere_retinaOd_exsudaten: Koi.boolean().allow(null),
    perifere_retinaOd_hypertensieveRetinopathie: Koi.boolean().allow(null),
    perifere_retinaOd_latticeDegeneratie: Koi.boolean().allow(null),
    perifere_retinaOd_microaneurysma: Koi.boolean().allow(null),
    perifere_retinaOd_naevus: Koi.boolean().allow(null),
    perifere_retinaOd_neovascularisatie: Koi.boolean().allow(null),
    perifere_retinaOd_netvliesdefect: Koi.boolean().allow(null),
    perifere_retinaOd_netvliesscheur: Koi.boolean().allow(null),
    perifere_retinaOd_venaStamocclusie: Koi.boolean().allow(null),
    perifere_retinaOd_venaTakocclusie: Koi.boolean().allow(null),
    perifere_retinaOs_IRMA: Koi.boolean().allow(null),
    perifere_retinaOs_PEA: Koi.boolean().allow(null),
    perifere_retinaOs_ablatio: Koi.boolean().allow(null),
    perifere_retinaOs_andersCheck: Koi.boolean().allow(null),
    perifere_retinaOs_arterieleStamocclusie: Koi.boolean().allow(null),
    perifere_retinaOs_arterieleTakocclusie: Koi.boolean().allow(null),
    perifere_retinaOs_atherosclerosis: Koi.boolean().allow(null),
    perifere_retinaOs_beading: Koi.boolean().allow(null),
    perifere_retinaOs_bloeding: Koi.boolean().allow(null),
    perifere_retinaOs_cottonWoolSpots: Koi.boolean().allow(null),
    perifere_retinaOs_drusen: Koi.boolean().allow(null),
    perifere_retinaOs_exsudaten: Koi.boolean().allow(null),
    perifere_retinaOs_hypertensieveRetinopathie: Koi.boolean().allow(null),
    perifere_retinaOs_latticeDegeneratie: Koi.boolean().allow(null),
    perifere_retinaOs_microaneurysma: Koi.boolean().allow(null),
    perifere_retinaOs_naevus: Koi.boolean().allow(null),
    perifere_retinaOs_neovascularisatie: Koi.boolean().allow(null),
    perifere_retinaOs_netvliesdefect: Koi.boolean().allow(null),
    perifere_retinaOs_netvliesscheur: Koi.boolean().allow(null),
    perifere_retinaOs_venaStamocclusie: Koi.boolean().allow(null),
    perifere_retinaOs_venaTakocclusie: Koi.boolean().allow(null),
});

const optometrischeDiagnoseValidation = Koi.object().keys({
    cataract: Koi.boolean(),
    droge_ogen: Koi.boolean(),
    glaucoom: Koi.boolean(),
    glaucoom_ja: Koi.boolean(),
    glaucoom_nee: Koi.boolean(),
    glaucoom_suspect: Koi.boolean(),
    macula_degeneratie: Koi.boolean(),
    macula_degeneratie_droog: Koi.boolean(),
    macula_degeneratie_nat: Koi.boolean(),
    macula_degeneratie_suspect: Koi.boolean(),
    refractie_problemen: Koi.boolean(),
    retinopathie: Koi.boolean(),
    retinopathie_diabetische: Koi.boolean(),
    retinopathie_overig: Koi.boolean(),
});

export const tooV1DraftDiagnosticReport = Koi.object().keys({
    afwijking_in_ao: Koi.any().allow(null),
    afwijking_in_vo: Koi.any().allow(null),
    conclusie_ao: Koi.any().allow(null),
    conclusie_diagnose: Koi.any().allow(null),
    conclusie_vo: Koi.any().allow(null),
    geadviseerd_beleid: Koi.any().allow(null),
    geadviseerd_beleid_anders: Koi.any().allow(null),
    optometrische_diagnose: Koi.any().allow(null),
    toelichting: Koi.any().allow(null),
    urgentie: Koi.any().allow(null),
});

export const tooV1FinalDiagnosticReport = Koi.object().keys({
    afwijking_in_ao: Koi.when('conclusie_ao', {
        is: 'Afwijkend',
        otherwise: Koi.allow(null),
        then: afwijkingInAchtersteOogsegmentValidation,
    }),
    afwijking_in_vo: Koi.when('conclusie_vo', {
        is: 'Afwijkend',
        otherwise: Koi.allow(null),
        then: afwijkingInVoorsteOogsegmentValidation,
    }),
    conclusie_ao: Koi.valid('Normaal', 'Afwijkend'),
    conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption),
    conclusie_vo: Koi.valid('Normaal', 'Afwijkend'),
    geadviseerd_beleid: Koi.koi().enum(OphthalmologyPoliciesEnum),
    geadviseerd_beleid_anders: Koi.when('geadviseerd_beleid', {
        is: OphthalmologyPoliciesEnum.anders,
        otherwise: Koi.valid(null),
        then: Koi.string().max(255).required(),
    }),
    optometrische_diagnose: optometrischeDiagnoseValidation,
    toelichting: Koi.string().max(1000).allow(null),
    urgentie: Koi.when('geadviseerd_beleid', {
        is: OphthalmologyPoliciesEnum.revisieOptometrist,
        otherwise: Koi.when('geadviseerd_beleid', {
            is: Koi.valid(
                OphthalmologyPoliciesEnum.consultOogartsDoorHuisarts,
                OphthalmologyPoliciesEnum.consultOogartsDoorOptometrist
            ),
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(UrgenciesForConsultOogartsNoAndersEnum),
        }),
        then: Koi.string().regex(UrgenciesForRevisionOptometristRegex),
    }),
});

export const tfcV1FinalRequest = Koi.object().keys({
    conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
    diabetes_sinds: Koi.koi().date().allow(null),
    files: Koi.array().items(ophthalmologyFile),
    gereguleerd: Koi.string().valid('Ja', 'Nee').allow(null),
    toelichting: Koi.string().max(1000).allow(null),
    vraag: Koi.string().max(1000),
});

export const tfcV1DraftRequest = tfcV1FinalRequest.concat(
    Koi.object({
        conclusie_diagnose: Koi.any().allow(null),
        vraag: Koi.any().allow(null),
    })
);

const tfcV1FinalDiagnosticReportBevindingen = Koi.object().keys({
    andersText: Koi.string().max(255).allow(null),
    andersText_od: Koi.boolean().allow(null),
    andersText_os: Koi.boolean().allow(null),
    bloedinkjes: Koi.boolean().allow(null),
    bloedinkjes_od: Koi.boolean().allow(null),
    bloedinkjes_os: Koi.boolean().allow(null),
    cotton_wool_spots: Koi.boolean().allow(null),
    cotton_wool_spots_od: Koi.boolean().allow(null),
    cotton_wool_spots_os: Koi.boolean().allow(null),
    exsudaten: Koi.boolean().allow(null),
    exsudaten_od: Koi.boolean().allow(null),
    exsudaten_os: Koi.boolean().allow(null),
    geen_afwijkingen: Koi.boolean().allow(null),
    geen_afwijkingen_od: Koi.boolean().allow(null),
    geen_afwijkingen_os: Koi.boolean().allow(null),
    niet_te_beoordelen: Koi.boolean().allow(null),
    niet_te_beoordelen_od: Koi.boolean().allow(null),
    niet_te_beoordelen_os: Koi.boolean().allow(null),
});

export enum TfcV1DiagnosticReportBezoekAanSpecialist {
    'ja' = 'Ja',
    'nee' = 'Nee',
    'zieBeleid' = 'Zie beleid',
}

export const tfcV1FinalDiagnosticReport = fundusscreeningUrgencyValidation.concat(
    Koi.object().keys({
        bevindingen: tfcV1FinalDiagnosticReportBevindingen,
        bezoek_aan_specialist_nu_nodig: Koi.koi().enum(TfcV1DiagnosticReportBezoekAanSpecialist),
        conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption),
        geadviseerd_beleid: Koi.when('bezoek_aan_specialist_nu_nodig', {
            is: TfcV1DiagnosticReportBezoekAanSpecialist.zieBeleid,
            otherwise: Koi.koi().enum(Policies).allow(null),
            then: Koi.koi().enum(Policies),
        }),
        geadviseerd_beleid_anders: Koi.when('geadviseerd_beleid', {
            is: Policies.anders,
            otherwise: Koi.valid(null),
            then: Koi.string().max(255).required(),
        }),
    })
);

export const tfcV1DraftDiagnosticReport = tfcV1FinalDiagnosticReport.concat(
    Koi.object().keys({
        bevindingen: Koi.allow(null),
        bezoek_aan_specialist_nu_nodig: Koi.allow(null),
        conclusie_diagnose: Koi.allow(null),
        geadviseerd_beleid: Koi.allow(null),
        geadviseerd_beleid_anders: Koi.allow(null),
        urgentie: Koi.allow(null),
    })
);

export const tovV1FinalRequest = Koi.object().keys({
    co_morbiditeiten: Koi.string().max(10000).allow(null),
    toelichting: Koi.string().max(10000).allow(null),
    verwijsreden: Koi.string().max(10000).allow(null),
    voorgeschiedenis: Koi.string().max(10000).allow(null),
});

export const tovV1IntakeReport = Koi.object().keys({
    intake: Koi.string().max(100000).allow(null),
});

export const tovV1DischargeReport = Koi.object().keys({
    dischargeLetter: Koi.string().max(100000).allow(null),
});

export interface ITocV1DiagnosticReportCustomControlDataWerkdiagnose {
    cataract: boolean;
    diabetische_retinopathie: boolean;
    droge_ogen: boolean;
    glaucoom: boolean;
    glaucoom_ja: boolean;
    glaucoom_nee: boolean;
    glaucoom_suspect: boolean;
    macula_degeneratie: boolean;
    macula_droog: boolean;
    macula_nat: boolean;
    macula_suspect: boolean;
    retinopathie: boolean;
    retinopathie_overig: boolean;
}

export interface ITocV1DiagnosticReportCustomControlDataBevindingen {
    andersText: string | null;
    andersText_od: boolean;
    andersText_os: boolean;
    bloedinkjes?: boolean;
    bloedinkjes_od?: boolean;
    bloedinkjes_os?: boolean;
    geen_afwijkingen: boolean;
    geen_afwijkingen_od: boolean;
    geen_afwijkingen_os: boolean;
    niet_te_beoordelen: boolean;
    niet_te_beoordelen_od: boolean;
    niet_te_beoordelen_os: boolean;
}

export interface ITocV1DiagnosticReportCustomControlData {
    antwoord: string | null;
    beleid: OphthalmologyPolicies;
    beleid_anders: string | null;
    bevindingen: ITocV1DiagnosticReportCustomControlDataBevindingen;
    bezoek_aan_specialist_nu_nodig: string | null;
    heb_je_een_diagnose: ConclusieDiagnoseOption;
    urgentie: UrgenciesForRevisionOptometrist | UrgenciesForConsultOogarts | null;
    urgentie_anders: string | null;
    werkdiagnose: ITocV1DiagnosticReportCustomControlDataWerkdiagnose;
}

export interface ITfcV1DiagnosticReportCustomControlDataBevindingen {
    [prop: string]: boolean | string | null;
    andersText: string | null;
    andersText_od: boolean;
    andersText_os: boolean;
    bloedinkjes: boolean;
    bloedinkjes_od: boolean;
    bloedinkjes_os: boolean;
    cotton_wool_spots: boolean;
    cotton_wool_spots_od: boolean;
    cotton_wool_spots_os: boolean;
    exsudaten: boolean;
    exsudaten_od: boolean;
    exsudaten_os: boolean;
    geen_afwijkingen: boolean;
    geen_afwijkingen_od: boolean;
    geen_afwijkingen_os: boolean;
    niet_te_beoordelen: boolean;
    niet_te_beoordelen_od: boolean;
    niet_te_beoordelen_os: boolean;
}

export const enum TooV1DiagnosticReportCustomControlDataConclusieOption {
    different = 'Afwijkend',
    normal = 'Normaal',
}

export interface ITooDiagnosticReportCustomControlDataOptometrischeDiagnose {
    [key: string]: boolean;
    cataract: boolean;
    droge_ogen: boolean;
    glaucoom: boolean;
    glaucoom_ja: boolean;
    glaucoom_nee: boolean;
    glaucoom_suspect: boolean;
    macula_degeneratie: boolean;
    macula_degeneratie_droog: boolean;
    macula_degeneratie_nat: boolean;
    macula_degeneratie_suspect: boolean;
    refractie_problemen: boolean;
    retinopathie: boolean;
    retinopathie_diabetische: boolean;
    retinopathie_overig: boolean;
}

export type TooV1FinalRequest = {
    familiaire_voorgeschiedenis: string | null;
    files: IAsset[];
    oculaire_voorgeschiedenis: string | null;
    omschrijf_de_duur_van_de_klachten: string | null;
    patientEmailAddress: string | null;
    reden_van_verwijzing: ITooRequestCustomControlDataRedenVanVerwijzing;
    relevante_medische_voorgeschiedenis: string | null;
    toelichting: string | null;
};

export interface ITooRequestCustomControlDataRedenVanVerwijzing {
    andersText: string | null;
    dubbel_zien: boolean;
    gezichtsveldklachten: boolean;
    hoofdpijn: boolean;
    prikken_jeuken_tranen: boolean;
    risicofactoren_glaucoom: boolean;
    rood_oog_droog_oog: boolean;
    verdenking_op_cataract: boolean;
    vervormd_zien: boolean;
    visusveranderingen: boolean;
    wazig_zien: boolean;
}
export const tooRequestCustomControlDataRedenVanVerwijzingLabels: { [key: string]: string } = {
    dubbel_zien: 'Dubbel zien',
    gezichtsveldklachten: 'Gezichtsveldklachten',
    hoofdpijn: 'Hoofdpijn',
    prikken_jeuken_tranen: 'Prikken, jeuken, tranen',
    risicofactoren_glaucoom: 'Risicofactoren glaucoom',
    rood_oog_droog_oog: 'Rood oog, droog oog',
    verdenking_op_cataract: 'Verdenking op cataract',
    vervormd_zien: 'Vervormd zien',
    visusveranderingen: 'Visusveranderingen (langzaam)',
    wazig_zien: 'Wazig zien',
};

export interface ITooProcedureCustomControlData {
    aanvullend_onderzoek: string[];
    aanvullend_onderzoek_od: (string | null)[];
    aanvullend_onderzoek_os: (string | null)[];
    algemene_gezondheid_en_medicatie: string | null;
    amsler_test_od: TooAmslerTest | null;
    amsler_test_os: TooAmslerTest | null;
    autorefractie_as_od: string | null;
    autorefractie_as_os: string | null;
    autorefractie_cylindrisch_od: string | null;
    autorefractie_cylindrisch_os: string | null;
    autorefractie_sferisch_od: string | null;
    autorefractie_sferisch_os: string | null;
    break_up_time_od: string | null;
    break_up_time_os: string | null;
    cycloplegische_as_od: string | null;
    cycloplegische_as_os: string | null;
    cycloplegische_cylindrisch_od: string | null;
    cycloplegische_cylindrisch_os: string | null;
    cycloplegische_sferisch_od: string | null;
    cycloplegische_sferisch_os: string | null;
    draagt_correctie: string | null;
    eigen_correctie_as_od: string | null;
    eigen_correctie_as_os: string | null;
    eigen_correctie_cylindrish_od: string | null;
    eigen_correctie_cylindrish_os: string | null;
    eigen_correctie_sferisch_od: string | null;
    eigen_correctie_sferisch_os: string | null;
    ervaart_klachten_als_storend: string | null;
    familiaire_voorgeschiedenis: string | null;
    files: IOphthalmologyAsset[] | [];
    gonioscopie_od: string | null;
    gonioscopie_os: string | null;
    gonioscopie_toelichting_od: string | null;
    gonioscopie_toelichting_os: string | null;
    huisarts_betrokkenheid: string | null;
    huisarts_is_betrokken: boolean | null;
    isPatientInformed: boolean;
    klachten: string | null;
    nabije_visus_od: string | null;
    nabije_visus_os: string | null;
    oculaire_voorgeschiedenis: string | null;
    oogdruk_od: string | null;
    oogdruk_os: string | null;
    pachymetrie_od: string | null;
    pachymetrie_os: string | null;
    protocol: TooProtocol | null;
    reden_van_bezoek: string | null;
    reden_van_bezoek_overig: string | null;
    refractie_as_od: string | null;
    refractie_as_os: string | null;
    refractie_cylindrish_od: string | null;
    refractie_cylindrish_os: string | null;
    refractie_sferisch_od: string | null;
    refractie_sferisch_os: string | null;
    rijdt_auto: string | null;
    stenopeische_visus_od: string | null;
    stenopeische_visus_os: string | null;
    verwijsbrief_beschikbaar: boolean | null;
    verwijzer: TooVerwijzer | null;
    verwijzer_overig: string | null;
    visus_od: string | null;
    visus_os: string | null;
    visus_refractie_od: string | null;
    visus_refractie_os: string | null;
}

export interface IOphthalmologyAsset {
    filename: string;
    id?: string;
    placement: OphthalmologyFilePlacement;
    s3Id?: string;
}

export interface ITfoProcedureCustomControlData {
    files: IOphthalmologyAsset[];
    hoe_gemeten: string | null;
    oogdruk_od: string | null;
    oogdruk_os: string | null;
    patient_gedruppeld: string | null;
    visus_od: string | null;
    visus_os: string | null;
}

export interface ITfoDiagnosticReportCustomControlData {
    andere_bevindingen_od: boolean | null;
    andere_bevindingen_od_text: string | null;
    andere_bevindingen_os: boolean | null;
    andere_bevindingen_os_text: string | null;
    bevindingen_npdr_od?: TfoOldScreeningStandard | null;
    bevindingen_npdr_os?: TfoOldScreeningStandard | null;
    geadviseerd_beleid: string | null;
    geadviseerd_beleid_anders: string | null;
    maculopathie_od?: Maculopathie | null;
    maculopathie_os?: Maculopathie | null;
    retinopathie_od?: Retinopathie | null;
    retinopathie_os?: Retinopathie | null;
    toelichting: string | null;
    urgentie: string | null;
}

export interface ITocRequestData {
    evaluation: {
        otherwiseReferPatient: YesOrNo | null;
    };
    question: string;
    selectedDiagnosticReportProviderOrganizationId: string;
    selectedDiagnosticReportProviderPractitionerId: string;
    toelichting: string | null;
}

export type OrthV1FinalRequest = {
    familiaire_voorgeschiedenis: string | null;
    files: IAsset[];
    isPatientInformed: boolean;
    oculaire_voorgeschiedenis: string | null;
    omschrijf_de_duur_van_de_klachten: string | null;
    reden_van_verwijzing: IOrthopticsRequestCustomControlDataRedenVanVerwijzing;
    relevante_medische_voorgeschiedenis: string | null;
    toelichting: string | null;
};

export interface IOrthopticsRequestCustomControlDataRedenVanVerwijzing {
    andersText: string | null;
    asthenope_klachten: boolean;
    geleidelijk_ontstaan_langer_bestaand_binoculair_dubbelzien: boolean;
    geleidelijke_daling_gezichtsscherpte_bij_kind: boolean;
    hoofdpijn: boolean;
    scheelzien_altijd_geleidelijk: boolean;
    vervolg_onderzoek: boolean;
}

export const orthOV1RedenVanVerwijzing = Koi.object().keys({
    andersText: Koi.string().max(1000).allow(null),
    asthenope_klachten: Koi.boolean().allow(null),
    geleidelijk_ontstaan_langer_bestaand_binoculair_dubbelzien: Koi.boolean().allow(null),
    geleidelijke_daling_gezichtsscherpte_bij_kind: Koi.boolean().allow(null),
    hoofdpijn: Koi.boolean().allow(null),
    scheelzien_altijd_geleidelijk: Koi.boolean().allow(null),
});

export const orthOFolV1RedenVanVerwijzing = orthOV1RedenVanVerwijzing.keys({
    vervolg_onderzoek: Koi.boolean().allow(null),
});

export const orthOV1DraftRequest = Koi.object({
    familiaire_voorgeschiedenis: Koi.string().max(10000).allow(null),
    files: assets.optional(),
    isPatientInformed: Koi.boolean(),
    oculaire_voorgeschiedenis: Koi.string().max(10000).allow(null),
    omschrijf_de_duur_van_de_klachten: Koi.string().max(10000).allow(null),
    reden_van_verwijzing: orthOV1RedenVanVerwijzing,
    relevante_medische_voorgeschiedenis: Koi.string().max(10000).allow(null),
    toelichting: Koi.string().max(10000).allow(null),
});

export const orthOV1FinalRequest = orthOV1DraftRequest.concat(
    Koi.object().keys({
        reden_van_verwijzing: orthOV1RedenVanVerwijzing.anyFilledIn(),
    })
);

export const orthOFolV1DraftRequest = Koi.object({
    familiaire_voorgeschiedenis: Koi.string().max(10000).allow(null),
    isPatientInformed: Koi.boolean(),
    oculaire_voorgeschiedenis: Koi.string().max(10000).allow(null),
    omschrijf_de_duur_van_de_klachten: Koi.string().max(10000).allow(null),
    reden_van_verwijzing: orthOFolV1RedenVanVerwijzing,
    relevante_medische_voorgeschiedenis: Koi.string().max(10000).allow(null),
    toelichting: Koi.string().max(10000).allow(null),
});

export const orthOFolV1FinalRequest = orthOFolV1DraftRequest.concat(
    Koi.object().keys({
        reden_van_verwijzing: orthOFolV1RedenVanVerwijzing.anyFilledIn(),
    })
);

export const orthopticsRequestCustomControlDataRedenVanVerwijzingLabels: { [key: string]: string } = {
    asthenope_klachten: 'Asthenope klachten',
    geleidelijk_ontstaan_langer_bestaand_binoculair_dubbelzien:
        'Geleidelijk ontstaan/langer bestaand binoculair dubbelzien',
    geleidelijke_daling_gezichtsscherpte_bij_kind: 'Geleidelijke daling gezichtsscherpte bij kind',
    hoofdpijn: 'Hoofdpijn',
    scheelzien_altijd_geleidelijk: 'Scheelzien altijd/geleidelijk',
    vervolg_onderzoek: 'Vervolgonderzoek orthoptie',
};

export enum OrthOVerwijzer {
    consultationBureau = 'consultationBureau',
    generalPractitioner = 'generalPractitioner',
    optician = 'optician',
    optometrist = 'optometrist',
    other = 'other',
    ownInitiative = 'ownInitiative',
    schoolDoctor = 'schoolDoctor',
}

export enum OogmotiliteitOption {
    bijzonderheden = 'Bijzonderheden',
    geenBijzonderheden = 'Geen bijzonderheden',
}

export const orthODraftLeidseSchema = Koi.object({
    leidse_schema_1: Koi.string().max(250).allow(null),
    leidse_schema_2: Koi.string().max(250).allow(null),
    leidse_schema_3: Koi.string().max(250).allow(null),
    leidse_schema_4: Koi.string().max(250).allow(null),
    leidse_schema_5: Koi.string().max(250).allow(null),
    leidse_schema_6: Koi.string().max(250).allow(null),
    leidse_schema_7: Koi.string().max(250).allow(null),
    leidse_schema_8: Koi.string().max(250).allow(null),
    leidse_schema_9: Koi.string().max(250).allow(null),
    leidse_schema_10: Koi.string().max(250).allow(null),
    leidse_schema_patroon: Koi.string().max(250).allow(null),
});

const orthOV1FinalProcedureFieldsExceptVerwijzer = Koi.object({
    accommodatie_od: Koi.string().max(10000).allow(null, ''),
    accommodatie_ods: Koi.string().max(10000).allow(null, ''),
    accommodatie_os: Koi.string().max(10000).allow(null, ''),
    algemene_gezondheid_en_medicatie: Koi.string().max(10000),
    algemene_observatie_gezicht: Koi.string().max(10000),
    autorefractie_additie_od: Koi.string().max(250).allow(null, ''),
    autorefractie_additie_os: Koi.string().max(250).allow(null, ''),
    autorefractie_as_od: Koi.string().max(250).allow(null, ''),
    autorefractie_as_os: Koi.string().max(250).allow(null, ''),
    autorefractie_cyclo_additie_od: Koi.string().max(250).allow(null, ''),
    autorefractie_cyclo_additie_os: Koi.string().max(250).allow(null, ''),
    autorefractie_cyclo_as_od: Koi.string().max(250).allow(null, ''),
    autorefractie_cyclo_as_os: Koi.string().max(250).allow(null, ''),
    autorefractie_cyclo_cylindrisch_od: Koi.string().max(250).allow(null, ''),
    autorefractie_cyclo_cylindrisch_os: Koi.string().max(250).allow(null, ''),
    autorefractie_cyclo_prisma_degrees_od: Koi.number().min(0).max(360).allow(null, ''),
    autorefractie_cyclo_prisma_degrees_os: Koi.number().min(0).max(360).allow(null, ''),
    autorefractie_cyclo_prisma_od: Koi.string().max(250).allow(null, ''),
    autorefractie_cyclo_prisma_os: Koi.string().max(250).allow(null, ''),
    autorefractie_cyclo_sferisch_od: Koi.string().max(250).allow(null, ''),
    autorefractie_cyclo_sferisch_os: Koi.string().max(250).allow(null, ''),
    autorefractie_cylindrisch_od: Koi.string().max(250).allow(null, ''),
    autorefractie_cylindrisch_os: Koi.string().max(250).allow(null, ''),
    autorefractie_prisma_degrees_od: Koi.number().min(0).max(360).allow(null, ''),
    autorefractie_prisma_degrees_os: Koi.number().min(0).max(360).allow(null, ''),
    autorefractie_prisma_od: Koi.string().max(250).allow(null, ''),
    autorefractie_prisma_os: Koi.string().max(250).allow(null, ''),
    autorefractie_sferisch_od: Koi.string().max(250).allow(null, ''),
    autorefractie_sferisch_os: Koi.string().max(250).allow(null, ''),
    convergentietest: Koi.string().max(10000),
    covertest_6m: Koi.string().max(10000),
    covertest_30cm: Koi.string().max(10000),
    dynamische_skiascopie_onderzoek: Koi.string().max(250).allow(null, ''),
    extra_aanvullende_onderzoeken: Koi.array().items(
        Koi.object({ findings: Koi.string().max(10000).allow(null, ''), name: Koi.string().max(10000).allow(null, '') })
    ),
    familiaire_voorgeschiedenis: Koi.string().max(10000),
    files: Koi.array().items(ophthalmologyFile),
    four_dot_test: Koi.string().max(10000).allow(null, ''),
    glazen_bagolini: Koi.string().max(10000).allow(null, ''),
    hasConsultRequestFields: Koi.boolean(),
    huidige_brilsterkte_od: Koi.string().max(10000).allow(null, ''),
    huidige_brilsterkte_os: Koi.string().max(10000).allow(null, ''),
    isPatientInformed: Koi.boolean(),
    klachten_en_of_reden_van_bezoek: Koi.string().max(10000),
    leidse_schema_toelichting: Koi.string().max(10000).allow(null, ''),
    lichtreflex: Koi.string().max(10000),
    nabije_visus_onderzoek_od: Koi.string().max(250).allow(null, ''),
    nabije_visus_onderzoek_ods: Koi.string().max(250).allow(null, ''),
    nabije_visus_onderzoek_os: Koi.string().max(250).allow(null, ''),
    oculaire_voorgeschiedenis: Koi.string().max(10000),
    oogbewegingen: Koi.koi().enum(OogmotiliteitOption),
    prismacovertest_6m: Koi.string().max(10000).allow(null, ''),
    prismacovertest_30cm: Koi.string().max(10000).allow(null, ''),
    prismafusie_horizontaal_6m: Koi.string().max(10000).allow(null, ''),
    prismafusie_horizontaal_30cm: Koi.string().max(10000).allow(null, ''),
    prismafusie_verticaal_6m: Koi.string().max(10000).allow(null, ''),
    prismafusie_verticaal_30cm: Koi.string().max(10000).allow(null, ''),
    prismatest_15: Koi.string().max(10000).allow(null, ''),
    prismatest_omgekeerde: Koi.string().max(10000).allow(null, ''),
    pupil_reacties: Koi.string().max(10000).allow(null, ''),
    refractie_additie_od: Koi.string().max(250).allow(null, ''),
    refractie_additie_os: Koi.string().max(250).allow(null, ''),
    refractie_as_od: Koi.string().max(250).allow(null, ''),
    refractie_as_os: Koi.string().max(250).allow(null, ''),
    refractie_cylindrisch_od: Koi.string().max(250).allow(null, ''),
    refractie_cylindrisch_os: Koi.string().max(250).allow(null, ''),
    refractie_prisma_degrees_od: Koi.number().min(0).max(360).allow(null, ''),
    refractie_prisma_degrees_os: Koi.number().min(0).max(360).allow(null, ''),
    refractie_prisma_od: Koi.string().max(250).allow(null, ''),
    refractie_prisma_os: Koi.string().max(250).allow(null, ''),
    refractie_sferisch_od: Koi.string().max(250).allow(null, ''),
    refractie_sferisch_os: Koi.string().max(250).allow(null, ''),
    skiascopie_cyclo_as_od: Koi.string().max(250).allow(null, ''),
    skiascopie_cyclo_as_os: Koi.string().max(250).allow(null, ''),
    skiascopie_cyclo_cylindrisch_od: Koi.string().max(250).allow(null, ''),
    skiascopie_cyclo_cylindrisch_os: Koi.string().max(250).allow(null, ''),
    skiascopie_cyclo_sferisch_od: Koi.string().max(250).allow(null, ''),
    skiascopie_cyclo_sferisch_os: Koi.string().max(250).allow(null, ''),
    spleetlamp_onderzoek_od: Koi.string().max(250).allow(null, ''),
    spleetlamp_onderzoek_os: Koi.string().max(250).allow(null, ''),
    stereotest: Koi.string().max(10000),
    visus_pinhole_od: Koi.string().max(250).allow(null, ''),
    visus_pinhole_os: Koi.string().max(250).allow(null, ''),
});

export const orthOFolV1FinalProcedureFieldsExceptVerwijzer = orthOV1FinalProcedureFieldsExceptVerwijzer.concat(
    Koi.object().keys({
        algemene_gezondheid_en_medicatie: Koi.string().max(10000).allow(null, ''),
        convergentietest: Koi.string().max(10000).allow(null, ''),
        four_dot_test: Koi.string().max(10000).allow(null, ''),
        huidige_brilsterkte_od: Koi.string().max(10000).allow(null, ''),
        huidige_brilsterkte_os: Koi.string().max(10000).allow(null, ''),
        lichtreflex: Koi.string().max(10000).allow(null, ''),
        oculaire_voorgeschiedenis: Koi.string().max(10000).allow(null, ''),
        oogbewegingen: Koi.koi().enum(OogmotiliteitOption).allow(null),
        prismacovertest_6m: Koi.string().max(10000).allow(null, ''),
        prismacovertest_30cm: Koi.string().max(10000).allow(null, ''),
        prismatest_15: Koi.string().max(10000).allow(null, ''),
        prismatest_omgekeerde: Koi.string().max(10000).allow(null, ''),
    })
);

function getSchemaForVisusField(
    version: 'draft' | 'final',
    visusGroup: 'visus_sc' | 'visus_cc' | 'visus_ec' | 'visus_bcva',
    dependantOnOtherFieldName?: string
) {
    if (version === 'draft') {
        return Koi.string().max(250).allow(null, '');
    } else if (dependantOnOtherFieldName) {
        return Koi.when('visusGroup.' + visusGroup, {
            is: true,
            otherwise: Koi.string().max(250).allow(null, ''),
            then: Koi.when(dependantOnOtherFieldName, {
                is: Koi.any().valid(null, ''),
                otherwise: Koi.string().max(250),
                then: Koi.string().max(250).allow(null, ''),
            }),
        });
    } else {
        return Koi.when('visusGroup.' + visusGroup, {
            is: true,
            otherwise: Koi.string().max(250).allow(null, ''),
            then: Koi.string().max(250),
        });
    }
}

function getSchemaForVisusGroup(version: 'draft' | 'final') {
    const schema = Koi.object({
        visus_bcva: Koi.boolean().allow(null),
        visus_cc: Koi.boolean().allow(null),
        visus_ec: Koi.boolean().allow(null),
        visus_sc: Koi.boolean().allow(null),
    });
    if (version === 'draft') {
        return schema;
    } else {
        return schema.anyFilledIn();
    }
}

function getPrismaValidationSchemas(
    prismaFieldName: string,
    prismaDegreesFieldName: string,
    version: 'draft' | 'final'
) {
    const schema = Koi.object({
        [prismaDegreesFieldName]: Koi.number().min(0).max(360).allow(null, ''),
        [prismaFieldName]: Koi.string().max(250).allow(null, ''),
    });
    if (version === 'final') {
        return schema.and(prismaFieldName, prismaDegreesFieldName, {
            isPresent: (resolved) => resolved !== null && resolved !== '',
        });
    }

    return schema;
}

function getSchemaForVisus(version: 'draft' | 'final') {
    return Koi.object({
        visus_bcva_od: getSchemaForVisusField(version, 'visus_bcva'),
        visus_bcva_ods: getSchemaForVisusField(version, 'visus_bcva'),
        visus_bcva_os: getSchemaForVisusField(version, 'visus_bcva'),
        visus_bcva_refractie_additie_od: Koi.string().max(250).allow(null, ''),
        visus_bcva_refractie_additie_os: Koi.string().max(250).allow(null, ''),
        visus_bcva_refractie_as_od: getSchemaForVisusField(
            version,
            'visus_bcva',
            'visus_bcva_refractie_cylindrisch_od'
        ),
        visus_bcva_refractie_as_os: getSchemaForVisusField(
            version,
            'visus_bcva',
            'visus_bcva_refractie_cylindrisch_os'
        ),
        visus_bcva_refractie_cylindrisch_od: getSchemaForVisusField(
            version,
            'visus_bcva',
            'visus_bcva_refractie_as_od'
        ),
        visus_bcva_refractie_cylindrisch_os: getSchemaForVisusField(
            version,
            'visus_bcva',
            'visus_bcva_refractie_as_os'
        ),
        visus_bcva_refractie_sferisch_od: getSchemaForVisusField(version, 'visus_bcva'),
        visus_bcva_refractie_sferisch_os: getSchemaForVisusField(version, 'visus_bcva'),
        visus_cc_od: getSchemaForVisusField(version, 'visus_cc'),
        visus_cc_ods: getSchemaForVisusField(version, 'visus_cc'),
        visus_cc_os: getSchemaForVisusField(version, 'visus_cc'),
        visus_cc_refractie_additie_od: Koi.string().max(250).allow(null, ''),
        visus_cc_refractie_additie_os: Koi.string().max(250).allow(null, ''),
        visus_cc_refractie_as_od: getSchemaForVisusField(version, 'visus_cc', 'visus_cc_refractie_cylindrisch_od'),
        visus_cc_refractie_as_os: getSchemaForVisusField(version, 'visus_cc', 'visus_cc_refractie_cylindrisch_os'),
        visus_cc_refractie_cylindrisch_od: getSchemaForVisusField(version, 'visus_cc', 'visus_cc_refractie_as_od'),
        visus_cc_refractie_cylindrisch_os: getSchemaForVisusField(version, 'visus_cc', 'visus_cc_refractie_as_os'),
        visus_cc_refractie_sferisch_od: getSchemaForVisusField(version, 'visus_cc'),
        visus_cc_refractie_sferisch_os: getSchemaForVisusField(version, 'visus_cc'),
        visus_ec_od: getSchemaForVisusField(version, 'visus_ec'),
        visus_ec_ods: getSchemaForVisusField(version, 'visus_ec'),
        visus_ec_os: getSchemaForVisusField(version, 'visus_ec'),
        visus_ec_refractie_additie_od: Koi.string().max(250).allow(null, ''),
        visus_ec_refractie_additie_os: Koi.string().max(250).allow(null, ''),
        visus_ec_refractie_as_od: getSchemaForVisusField(version, 'visus_ec', 'visus_ec_refractie_cylindrisch_od'),
        visus_ec_refractie_as_os: getSchemaForVisusField(version, 'visus_ec', 'visus_ec_refractie_cylindrisch_os'),
        visus_ec_refractie_cylindrisch_od: getSchemaForVisusField(version, 'visus_ec', 'visus_ec_refractie_as_od'),
        visus_ec_refractie_cylindrisch_os: getSchemaForVisusField(version, 'visus_ec', 'visus_ec_refractie_as_os'),
        visus_ec_refractie_sferisch_od: getSchemaForVisusField(version, 'visus_ec'),
        visus_ec_refractie_sferisch_os: getSchemaForVisusField(version, 'visus_ec'),
        visus_sc_od: getSchemaForVisusField(version, 'visus_sc'),
        visus_sc_ods: getSchemaForVisusField(version, 'visus_sc'),
        visus_sc_os: getSchemaForVisusField(version, 'visus_sc'),
        visusGroup: getSchemaForVisusGroup(version),
    })
        .concat(
            getPrismaValidationSchemas('visus_ec_refractie_prisma_od', 'visus_ec_refractie_prisma_degrees_od', version)
        )
        .concat(
            getPrismaValidationSchemas('visus_ec_refractie_prisma_os', 'visus_ec_refractie_prisma_degrees_os', version)
        )
        .concat(
            getPrismaValidationSchemas(
                'visus_bcva_refractie_prisma_od',
                'visus_bcva_refractie_prisma_degrees_od',
                version
            )
        )
        .concat(
            getPrismaValidationSchemas(
                'visus_bcva_refractie_prisma_os',
                'visus_bcva_refractie_prisma_degrees_os',
                version
            )
        )
        .concat(
            getPrismaValidationSchemas('visus_cc_refractie_prisma_od', 'visus_cc_refractie_prisma_degrees_od', version)
        )
        .concat(
            getPrismaValidationSchemas('visus_cc_refractie_prisma_os', 'visus_cc_refractie_prisma_degrees_os', version)
        );
}

export const orthCV1FinalRequest = Koi.object().keys({
    evaluation: Koi.object({
        otherwiseReferPatient: Koi.koi().enum(YesOrNo).allow(null),
    }),
    toelichting: Koi.string().max(1000).allow(null),
});

export const orthCV1DraftRequest = orthCV1FinalRequest;

const orthCV1DraftRequestForOrthO = orthCV1DraftRequest.concat(
    Koi.object().keys({
        question: Koi.string().max(1000).allow(null),
        selectedDiagnosticReportProviderOrganizationId: Koi.string().allow(null),
        selectedDiagnosticReportProviderPractitionerId: Koi.string().allow(null),
    })
);

const orthCV1FinalRequestForOrthO = orthCV1FinalRequest.concat(
    Koi.object().keys({
        question: Koi.string().max(1000),
        selectedDiagnosticReportProviderOrganizationId: Koi.string(),
        selectedDiagnosticReportProviderPractitionerId: Koi.string().allow(null),
    })
);

export const orthOV1FinalProcedure = orthOV1FinalProcedureFieldsExceptVerwijzer
    .concat(
        Koi.object().keys({
            consult: Koi.when('hasConsultRequestFields', {
                is: true,
                otherwise: Koi.any().forbidden(),
                then: orthCV1FinalRequestForOrthO,
            }),
            leidse_schema: Koi.when('oogbewegingen', {
                is: OogmotiliteitOption.bijzonderheden,
                otherwise: orthODraftLeidseSchema,
                then: orthODraftLeidseSchema.anyFilledIn(),
            }),
        })
    )
    .concat(getSchemaForVisus('final'));

export const orthOV1DraftProcedure = orthOV1FinalProcedureFieldsExceptVerwijzer
    .concat(
        Koi.object({
            algemene_gezondheid_en_medicatie: Koi.string().max(10000).allow(null, ''),
            algemene_observatie_gezicht: Koi.string().max(10000).allow(null, ''),
            consult: Koi.when('hasConsultRequestFields', {
                is: true,
                otherwise: Koi.any().forbidden(),
                then: orthCV1DraftRequestForOrthO,
            }),
            convergentietest: Koi.string().max(10000).allow(null, ''),
            covertest_6m: Koi.string().max(10000).allow(null, ''),
            covertest_30cm: Koi.string().max(10000).allow(null, ''),
            familiaire_voorgeschiedenis: Koi.string().max(10000).allow(null, ''),
            isPatientInformed: Koi.boolean().optional(),
            klachten_en_of_reden_van_bezoek: Koi.string().max(10000).allow(null, ''),
            leidse_schema: orthODraftLeidseSchema,
            lichtreflex: Koi.string().max(10000).allow(null, ''),
            oculaire_voorgeschiedenis: Koi.string().max(10000).allow(null, ''),
            oogbewegingen: Koi.koi().enum(OogmotiliteitOption).allow(null),
            stereotest: Koi.string().max(10000).allow(null, ''),
            verwijzer: Koi.koi().enum(OrthOVerwijzer).allow(null),
            verwijzer_overig: Koi.string().max(1000).allow(null, ''),
        })
    )
    .concat(getSchemaForVisus('draft'));

export const orthOFolV1FinalProcedure = orthOFolV1FinalProcedureFieldsExceptVerwijzer
    .concat(
        Koi.object().keys({
            consult: Koi.when('hasConsultRequestFields', {
                is: true,
                otherwise: Koi.any().forbidden(),
                then: orthCV1FinalRequestForOrthO,
            }),
            leidse_schema: Koi.when('oogbewegingen', {
                is: OogmotiliteitOption.bijzonderheden,
                otherwise: orthODraftLeidseSchema,
                then: orthODraftLeidseSchema.anyFilledIn(),
            }),
        })
    )
    .concat(getSchemaForVisus('final'));

export const orthOFolV1DraftProcedure = orthOFolV1FinalProcedureFieldsExceptVerwijzer
    .concat(
        Koi.object({
            algemene_observatie_gezicht: Koi.string().max(10000).allow(null, ''),
            consult: Koi.when('hasConsultRequestFields', {
                is: true,
                otherwise: Koi.any().forbidden(),
                then: orthCV1DraftRequestForOrthO,
            }),
            covertest_6m: Koi.string().max(10000).allow(null, ''),
            covertest_30cm: Koi.string().max(10000).allow(null, ''),
            familiaire_voorgeschiedenis: Koi.string().max(10000).allow(null, ''),
            klachten_en_of_reden_van_bezoek: Koi.string().max(10000).allow(null, ''),
            leidse_schema: orthODraftLeidseSchema,
            stereotest: Koi.string().max(10000).allow(null, ''),
        })
    )
    .concat(getSchemaForVisus('draft'));

export const orthOV1FinalProcedureWithRequest = orthOV1FinalProcedure.concat(
    Koi.object().keys({
        verwijzer: Koi.koi().enum(OrthOVerwijzer),
        verwijzer_overig: Koi.when('verwijzer', {
            is: OrthOVerwijzer.other,
            otherwise: Koi.valid(null, ''),
            then: Koi.string().max(1000),
        }),
    })
);

export const orthODiagnosticReportCustomControlBevindingenLabels: { [key: string]: string } = {
    accommodatie_problemen: 'Accommodatieproblemen',
    amblyopie: 'Amblyopie',
    anders: 'Anders',
    asthopene_klachten: 'Asthopene klachten',
    convergentie_insufficientie: 'Convergentie insufficiëntie',
    diplopie: 'Diplopie',
    geen_afwijkingen: 'Geen afwijkingen',
    refractieafwijking: 'Refractieafwijking',
    strabismus: 'Strabismus',
};

export interface IOrthOV1DiagnosticReportCustomControlDataBevindingen {
    accommodatie_problemen: boolean;
    amblyopie: boolean;
    anders: boolean;
    andersText: string | null;
    asthopene_klachten: boolean;
    convergentie_insufficientie: boolean;
    diplopie: boolean;
    geen_afwijkingen: boolean;
    refractieafwijking: boolean;
    strabismus: boolean;
}

export interface IOrthOV1DiagnosticReportCustomControlDataOrthoptischeDiagnose {
    [key: string]: boolean;
    amblyopie: boolean;
    insufficientie_en_overmatige_convergentie: boolean;
    refractiestoornis: boolean;
    refractiestoornis_anisometropie: boolean;
    refractiestoornis_astigmatisme: boolean;
    refractiestoornis_hypermetropie: boolean;
    refractiestoornis_myopie: boolean;
    strabismus: boolean;
}

export const orthODiagnosticReportCustomControlOrthoptischeDiagnoseLabels: { [key: string]: string } = {
    amblyopie: 'Amblyopie',
    insufficientie_en_overmatige_convergentie: 'Insufficiëntie en overmatige convergentie',
    refractiestoornis_anisometropie: 'Refractiestoornis: Anisometropie',
    refractiestoornis_astigmatisme: 'Refractiestoornis: Astigmatisme',
    refractiestoornis_hypermetropie: 'Refractiestoornis: Hypermetropie',
    refractiestoornis_myopie: 'Refractiestoornis: Myopie',
    strabismus: 'Strabismus',
};

export enum BeleidOptions {
    anders = 'Anders',
    geen_vervolgactie = 'Geen vervolgactie',
    revisie_orthoptist = 'Revisie orthoptist',
    terugverwijzing_huisarts = 'Terugverwijzing huisarts',
    verwijzing_oogarts = 'Verwijzing oogarts',
    verwijzing_optometrist = 'Verwijzing optometrist',
}

export enum TherapieOptions {
    anders = 'Anders',
    brilrecept = 'Brilrecept',
    geen_therapie = 'Geen therapie',
    myopie_behandeling = 'Myopie behandeling',
    occlusie = 'Occlusie',
    strabismus = 'Strabismus operatie',
}

export enum UrgenciesForConsultOptometrist {
    anders = 'Anders',
    b_voorrangsverwijzing = 'B. Voorrangsverwijzing, binnen 2 weken',
    c1_consult_gewone_verwijzing = 'C1. Consult gewone verwijzing, binnen 1-3 maanden',
    c2_consult_gewone_verwijzing = 'C2. Consult gewone verwijzing, binnen 4-6 maanden',
}

const orthOV1TherapieBrilreceptValidation = Koi.string()
    .max(250)
    .when('therapie', {
        is: TherapieOptions.brilrecept,
        otherwise: Koi.allow(null, ''),
        then: Koi.disallow(null, ''),
    });

const orthOV1TherapieBrilreceptValidationAllowEmpty = Koi.string().max(250).allow(null, '');

const orthOV1TherapieBrilreceptDegreesValidationAllowEmpty = Koi.number().min(0).max(360).allow(null);

const orthOV1TherapieBrilReceptExaminationFinalValidation = Koi.object({
    brilrecept_additie_od: orthOV1TherapieBrilreceptValidationAllowEmpty,
    brilrecept_additie_os: orthOV1TherapieBrilreceptValidationAllowEmpty,
    brilrecept_as_od: orthOV1TherapieBrilreceptValidationAllowEmpty,
    brilrecept_as_os: orthOV1TherapieBrilreceptValidationAllowEmpty,
    brilrecept_cylindrisch_od: orthOV1TherapieBrilreceptValidationAllowEmpty,
    brilrecept_cylindrisch_os: orthOV1TherapieBrilreceptValidationAllowEmpty,
    brilrecept_prisma_degrees_od: orthOV1TherapieBrilreceptDegreesValidationAllowEmpty,
    brilrecept_prisma_degrees_os: orthOV1TherapieBrilreceptDegreesValidationAllowEmpty,
    brilrecept_prisma_od: orthOV1TherapieBrilreceptValidationAllowEmpty,
    brilrecept_prisma_os: orthOV1TherapieBrilreceptValidationAllowEmpty,
    brilrecept_sferisch_od: orthOV1TherapieBrilreceptValidation,
    brilrecept_sferisch_os: orthOV1TherapieBrilreceptValidation,
});

const orthOV1DraftBevindingenValidation = Koi.object({
    accommodatie_problemen: Koi.boolean(),
    amblyopie: Koi.boolean(),
    anders: Koi.boolean(),
    andersText: Koi.string()
        .max(1000)
        .when('anders', {
            is: true,
            otherwise: Koi.allow(null, ''),
            then: Koi.disallow(null, ''),
        }),
    asthopene_klachten: Koi.boolean(),
    convergentie_insufficientie: Koi.boolean(),
    diplopie: Koi.boolean(),
    geen_afwijkingen: Koi.boolean(),
    refractieafwijking: Koi.boolean(),
    strabismus: Koi.boolean(),
});

const orthOV1FinalBevindingenValidation = orthOV1DraftBevindingenValidation.anyFilledIn();

const orthOV1FinalDiagnoseValidation = Koi.object({
    heb_je_een_orthoptische_diagnose: Koi.koi().enum(ConclusieDiagnoseOption),
    orthoptische_diagnose: Koi.object({
        amblyopie: Koi.boolean(),
        insufficientie_en_overmatige_convergentie: Koi.boolean(),
        refractiestoornis: Koi.boolean(),
        refractiestoornis_anisometropie: Koi.boolean(),
        refractiestoornis_astigmatisme: Koi.boolean(),
        refractiestoornis_hypermetropie: Koi.boolean(),
        refractiestoornis_myopie: Koi.boolean(),
        strabismus: Koi.boolean(),
    }),
});

const orthOV1FinalTherapieValidation = Koi.object({
    geadviseerd_beleid: Koi.koi().enum(BeleidOptions),
    overige_relevante_informatie: Koi.string().max(1000).allow(null, ''),
    termijn: Koi.when('geadviseerd_beleid', {
        is: BeleidOptions.revisie_orthoptist,
        otherwise: Koi.allow(null),
        then: Koi.string().regex(UrgenciesForRevisionOptometristRegex).required(),
    }),
    therapie: Koi.koi().enum(TherapieOptions),
    toelichting_beleid: Koi.string()
        .max(1000)
        .when('geadviseerd_beleid', {
            is: BeleidOptions.anders,
            otherwise: Koi.allow(null, ''),
            then: Koi.disallow(null, ''),
        }),
    toelichting_therapie: Koi.string()
        .max(1000)
        .when('therapie', {
            is: TherapieOptions.anders,
            otherwise: Koi.allow(null, ''),
            then: Koi.disallow(null, ''),
        }),
    toelichting_urgentie: Koi.string()
        .max(1000)
        .when('urgentie_oogarts', {
            is: UrgenciesForConsultOogartsEnum.anders,
            then: Koi.disallow(null, ''),
        })
        .concat(
            Koi.string()
                .max(1000)
                .when('urgentie_optometrist', {
                    is: UrgenciesForConsultOptometrist.anders,
                    otherwise: Koi.allow(null, ''),
                    then: Koi.disallow(null, ''),
                })
        ),
    urgentie_oogarts: Koi.koi().when('geadviseerd_beleid', {
        is: BeleidOptions.verwijzing_oogarts,
        otherwise: Koi.allow(null),
        then: Koi.koi().enum(UrgenciesForConsultOogartsEnum),
    }),
    urgentie_optometrist: Koi.koi().when('geadviseerd_beleid', {
        is: BeleidOptions.verwijzing_optometrist,
        otherwise: Koi.allow(null, ''),
        then: Koi.koi().enum(UrgenciesForConsultOptometrist),
    }),
});

const orthOV1DraftTherapieValidation = Koi.object({
    geadviseerd_beleid: Koi.koi().enum(BeleidOptions).allow(null),
    overige_relevante_informatie: Koi.string().max(1000).allow(null, ''),
    termijn: Koi.string().regex(UrgenciesForRevisionOptometristRegex).allow(null),
    therapie: Koi.koi().enum(TherapieOptions).allow(null),
    toelichting_beleid: Koi.string().max(1000).allow(null, ''),
    toelichting_therapie: Koi.string().max(1000).allow(null, ''),
    toelichting_urgentie: Koi.string().max(1000).allow(null, ''),
    urgentie_oogarts: Koi.koi().enum(UrgenciesForConsultOogartsEnum).allow(null),
    urgentie_optometrist: Koi.koi().enum(UrgenciesForConsultOptometrist).allow(null),
});

export const orthOV1FinalDiagnosticReport = Koi.object({ bevindingen: orthOV1FinalBevindingenValidation })
    .concat(orthOV1FinalDiagnoseValidation)
    .concat(orthOV1FinalTherapieValidation)
    .concat(orthOV1TherapieBrilReceptExaminationFinalValidation);

export const orthOV1DraftDiagnosticReport = Koi.object({ bevindingen: orthOV1DraftBevindingenValidation })
    .concat(orthOV1FinalDiagnoseValidation)
    .concat(orthOV1DraftTherapieValidation)
    .concat(
        Koi.object({
            brilrecept_additie_od: Koi.string().max(250).allow(null, ''),
            brilrecept_additie_os: Koi.string().max(250).allow(null, ''),
            brilrecept_as_od: Koi.string().max(250).allow(null, ''),
            brilrecept_as_os: Koi.string().max(250).allow(null, ''),
            brilrecept_cylindrisch_od: Koi.string().max(250).allow(null, ''),
            brilrecept_cylindrisch_os: Koi.string().max(250).allow(null, ''),
            brilrecept_prisma_degrees_od: Koi.number().min(0).max(360).allow(null, ''),
            brilrecept_prisma_degrees_os: Koi.number().min(0).max(360).allow(null, ''),
            brilrecept_prisma_od: Koi.string().max(250).allow(null, ''),
            brilrecept_prisma_os: Koi.string().max(250).allow(null, ''),
            brilrecept_sferisch_od: Koi.string().max(250).allow(null, ''),
            brilrecept_sferisch_os: Koi.string().max(250).allow(null, ''),
            heb_je_een_orthoptische_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
        })
    );

const werkDiagnoseOptions = [
    'hypermetropie',
    'myopie',
    'anisometropie',
    'astigmatisme',
    'insufficientie_en_overmatige_convergentie',
    'strabismus',
    'amblyopie',
    'anders',
];

const bevindingenValidationOrthC = Koi.object().keys({
    andersText: Koi.string().max(255).allow(null),
    andersText_od: Koi.boolean().allow(null),
    andersText_os: Koi.boolean().allow(null),
    geen_afwijkingen: Koi.boolean().allow(null),
    geen_afwijkingen_od: Koi.boolean().allow(null),
    geen_afwijkingen_os: Koi.boolean().allow(null),
    niet_te_beoordelen: Koi.boolean().allow(null),
    niet_te_beoordelen_od: Koi.boolean().allow(null),
    niet_te_beoordelen_os: Koi.boolean().allow(null),
});

export type IOrthCV1DiagnosticReportCustomControlDataWerkdiagnose =
    | 'hypermetropie'
    | 'myopie'
    | 'anisometropie'
    | 'astigmatisme'
    | 'insufficientie_en_overmatige_convergentie'
    | 'strabismus'
    | 'amblyopie'
    | 'anders';

export interface IOrthCV1DiagnosticReportCustomControlDataBevindingen {
    andersText: string | null;
    andersText_od: boolean;
    andersText_os: boolean;
    geen_afwijkingen: boolean;
    geen_afwijkingen_od: boolean;
    geen_afwijkingen_os: boolean;
    niet_te_beoordelen: boolean;
    niet_te_beoordelen_od: boolean;
    niet_te_beoordelen_os: boolean;
}

export interface IOrthCV1DiagnosticReportCustomControlData {
    beleid: OphthalmologyPolicies | null;
    beleid_anders: string | null;
    bevindingen: IOrthCV1DiagnosticReportCustomControlDataBevindingen;
    bezoek_aan_specialist_nu_nodig: BezoekAanSpecialistOption | null;
    conclusie_diagnose: ConclusieDiagnoseOption | null;
    termijn: UrgenciesForRevisionOptometrist | null;
    urgentie_anders: string | null;
    urgentie_oogarts: UrgenciesForConsultOogarts | null;
    urgentie_optometrist: UrgenciesForRevisionOptometrist | null;
    werkdiagnose: IOrthCV1DiagnosticReportCustomControlDataWerkdiagnose | null;
}

export const orthCV1DiagnosticReport = Koi.object().keys({
    beleid: Koi.koi().enum(BeleidOptions),
    beleid_anders: Koi.when('beleid', {
        is: BeleidOptions.anders,
        otherwise: Koi.valid(null),
        then: Koi.string().max(1000).required(),
    }),
    bevindingen: bevindingenValidationOrthC,
    bezoek_aan_specialist_nu_nodig: Koi.koi().enum(BezoekAanSpecialistOption).allow(null),
    conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).required(),
    termijn: Koi.when('beleid', {
        is: BeleidOptions.revisie_orthoptist,
        otherwise: Koi.valid(null),
        then: Koi.string().regex(UrgenciesForRevisionOptometristRegex).required(),
    }),
    urgentie_anders: Koi.when('urgentie_oogarts', {
        is: UrgenciesForConsultOogartsEnum.anders,
        otherwise: Koi.when('urgentie_optometrist', {
            is: UrgenciesForConsultOptometrist.anders,
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000).required(),
        }),
        then: Koi.string().max(1000).required(),
    }),
    urgentie_oogarts: Koi.koi().when('beleid', {
        is: BeleidOptions.verwijzing_oogarts,
        otherwise: Koi.allow(null),
        then: Koi.koi().enum(UrgenciesForConsultOogartsEnum),
    }),
    urgentie_optometrist: Koi.koi().when('beleid', {
        is: BeleidOptions.verwijzing_optometrist,
        otherwise: Koi.allow(null),
        then: Koi.koi().enum(UrgenciesForConsultOptometrist),
    }),
    werkdiagnose: Koi.when('conclusie_diagnose', {
        is: ConclusieDiagnoseOption.ja,
        otherwise: Koi.allow(null),
        then: Koi.string()
            .valid(...werkDiagnoseOptions)
            .required(),
    }),
});

export const orthCV1DraftDiagnosticReport = orthCV1DiagnosticReport.concat(
    Koi.object().keys({
        beleid: Koi.koi().enum(BeleidOptions).allow(null),
        beleid_anders: Koi.string().max(1000).allow(null),
        conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
        termijn: Koi.string().regex(UrgenciesForRevisionOptometristRegex).allow(null),
        urgentie_anders: Koi.string().max(1000).allow(null),
        urgentie_oogarts: Koi.koi().enum(UrgenciesForConsultOogartsEnum).allow(null),
        urgentie_optometrist: Koi.koi().enum(UrgenciesForConsultOptometrist).allow(null),
        werkdiagnose: Koi.string()
            .valid(...werkDiagnoseOptions)
            .allow(null),
    })
);

export const orthCV1FinalDiagnosticReport = orthCV1DiagnosticReport.concat(
    Koi.object().keys({
        bevindingen: bevindingenValidationOrthC.anyFilledIn('niet_te_beoordelen', 'geen_afwijkingen', 'andersText'),
    })
);

export const orthOFolV1FinalDiagnosticReport = orthOV1FinalDiagnosticReport;
export const orthOFolV1DraftDiagnosticReport = orthOV1DraftDiagnosticReport;
